import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCaregivers, generateInviteCodes, getInviteCodes, deleteInviteCode, getNotes, deleteNote, saveNote, getAdminClients, createClient, updateClient, deleteClient, getAssessment, saveAssessment, agentGenerateNote, agentBatchGenerateNotes, getBatchStatus, parseAssessmentFile, parseEvvFile, parseStoredEvvReport, agentEvvGenerateNote, listTaReports, uploadTaReport, deleteTaReport, getSmsSettings, saveSmsSettings, sendTestSms, sendAdHocSms, getSmsLogs, getSchedule, upsertSchedule, deleteSchedule, runScheduleNow, listPriorAuths, uploadPriorAuth, confirmPriorAuth, deletePriorAuth, snoozePriorAuth, getExpiringPriorAuths, createUser, updateUser, resetUserPassword, setUserStatus, deleteUser } from '../utils/api';
import PasswordInput from '../components/PasswordInput';
import { fillBlankCareDetails } from '../utils/clientDetails';
import { useAutoRefresh } from '../utils/useAutoRefresh';

const SECTION_META = {
  'ai-agent':      { title: 'AI Agent Notes',      subtitle: 'Generate weekly progress notes automatically' },
  'ai-agent-evv':  { title: 'AI Agent T&A',        subtitle: 'Generate notes from Time & Attendance report visit times' },
  'create-note':   { title: 'Create Note',          subtitle: 'Create a progress note on behalf of a caregiver' },
  'clients':       { title: 'Client Management',    subtitle: 'Manage clients and caregiver assignments' },
  'prior-auths':   { title: 'Prior Authorizations',  subtitle: 'Track expiring authorizations and reminders' },
  'notes':         { title: 'All Notes',            subtitle: 'View, filter, and manage all progress notes' },
  'users':         { title: 'Users',                subtitle: 'Create accounts, generate invite codes, and manage roles, names, and passwords' },
  'sms-reminders': { title: 'SMS Reminders',        subtitle: 'Configure weekly progress note reminders per caregiver' },
};

// ─── Constants for SmsModal ───────────────────────────────────────────────────

const SMS_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SMS_TIMEZONES = [
  { value: 'America/Detroit',     label: 'Eastern (Detroit)' },
  { value: 'America/New_York',    label: 'Eastern (New York)' },
  { value: 'America/Chicago',     label: 'Central' },
  { value: 'America/Denver',      label: 'Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage',   label: 'Alaska' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii' },
];
const E164_RE = /^\+[1-9]\d{7,14}$/;

function fmtPhone(raw) {
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return raw;
}
function phoneErr(v) {
  return v && !E164_RE.test(v) ? 'Use E.164 format, e.g. +12485551234' : null;
}
const DEFAULT_SMS_TEMPLATE =
  'Hi {name}, submit your Progress Note for {week}: notes.homecareofmi.com. ' +
  'Late submissions delay payroll. Thank you, Home Care Associates of MI';

function emptySmsForm() {
  return { phoneNumber: '', smsEnabled: false, timezone: 'America/Detroit',
           reminderDay: 1, reminderTime: '09:00',
           maxRemindersPerWeek: 7,
           escalationEnabled: false, adminPhoneNumber: '',
           messageTemplate: DEFAULT_SMS_TEMPLATE };
}

function renderPreview(template, caregiver) {
  const now = new Date();
  const sun = new Date(now);
  sun.setDate(now.getDate() - now.getDay());
  const sat = new Date(sun);
  sat.setDate(sun.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const week = `${fmt(sun)}–${fmt(sat)}`;
  return (template || DEFAULT_SMS_TEMPLATE)
    .replace(/\{name\}/g, caregiver.firstName || 'Caregiver')
    .replace(/\{week\}/g, week)
    .replace(/\{url\}/g, 'notes.homecareofmi.com');
}

// ─── SMS Settings Modal ───────────────────────────────────────────────────────

function SmsModal({ caregiver, onClose, showToast }) {
  const [form, setForm] = useState(emptySmsForm());
  const [lastSmsSent, setLastSmsSent] = useState('');
  const [lastSmsStatus, setLastSmsStatus] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const templateRef = useRef(null);
  const savedFormRef = useRef(null);

  useEffect(() => {
    getSmsSettings(caregiver.username)
      .then(d => {
        const s = d.settings || {};
        const loaded = {
          phoneNumber:         s.phoneNumber || '',
          smsEnabled:          s.smsEnabled || false,
          timezone:            s.timezone || 'America/Detroit',
          reminderDay:         s.reminderDay ?? 1,
          reminderTime:        s.reminderTime || '09:00',
          maxRemindersPerWeek: s.maxRemindersPerWeek ?? 7,
          escalationEnabled:   s.escalationEnabled || false,
          adminPhoneNumber:    s.adminPhoneNumber || '',
          messageTemplate:     s.messageTemplate || DEFAULT_SMS_TEMPLATE,
        };
        setForm(loaded);
        savedFormRef.current = loaded;
        setLastSmsSent(s.lastSmsSent || '');
        setLastSmsStatus(s.lastSmsStatus || '');
      })
      .catch(() => showToast('Could not load SMS settings', 'error'))
      .finally(() => setLoading(false));
  }, [caregiver.username]); // runs once per user opened in modal

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const insertPlaceholder = (ph) => {
    const el = templateRef.current;
    if (!el) { set('messageTemplate', form.messageTemplate + ph); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    set('messageTemplate', form.messageTemplate.slice(0, start) + ph + form.messageTemplate.slice(end));
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + ph.length;
      el.focus();
    });
  };

  const handlePhoneBlur = (key) => {
    const fmt = fmtPhone(form[key]);
    if (fmt !== form[key]) set(key, fmt);
  };

  const isDirty = savedFormRef.current
    ? JSON.stringify(form) !== JSON.stringify(savedFormRef.current)
    : false;

  const handleCloseAttempt = () => {
    if (isDirty) setShowCloseConfirm(true);
    else onClose();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const err = phoneErr(form.phoneNumber) || (form.escalationEnabled && phoneErr(form.adminPhoneNumber));
    if (err) { showToast(err, 'error'); return; }
    setSaving(true);
    try {
      await saveSmsSettings(caregiver.username, form);
      savedFormRef.current = { ...form };
      showToast('SMS settings saved', 'success');
    } catch (ex) {
      showToast(ex.message || 'Could not save settings', 'error');
    } finally { setSaving(false); }
  };

  const handleSaveAndClose = async () => {
    const err = phoneErr(form.phoneNumber) || (form.escalationEnabled && phoneErr(form.adminPhoneNumber));
    if (err) { showToast(err, 'error'); setShowCloseConfirm(false); return; }
    setSaving(true);
    try {
      await saveSmsSettings(caregiver.username, form);
      savedFormRef.current = { ...form };
      onClose();
    } catch (ex) {
      showToast(ex.message || 'Could not save settings', 'error');
      setShowCloseConfirm(false);
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!form.phoneNumber) { showToast('Enter a phone number first', 'error'); return; }
    if (phoneErr(form.phoneNumber)) { showToast(phoneErr(form.phoneNumber), 'error'); return; }
    setTesting(true);
    try {
      await saveSmsSettings(caregiver.username, form);
      await sendTestSms(caregiver.username);
      showToast('Test SMS sent!', 'success');
      setLastSmsStatus('test-ok');
      setLastSmsSent(new Date().toISOString());
    } catch (ex) {
      showToast(ex.message || 'Test SMS failed', 'error');
    } finally { setTesting(false); }
  };

  const handleSend = async () => {
    if (!form.phoneNumber) { showToast('Enter a phone number first', 'error'); return; }
    if (phoneErr(form.phoneNumber)) { showToast(phoneErr(form.phoneNumber), 'error'); return; }
    setSending(true);
    try {
      await saveSmsSettings(caregiver.username, form);
      await sendAdHocSms(caregiver.username);
      showToast('SMS reminder sent!', 'success');
      setLastSmsStatus('delivered');
      setLastSmsSent(new Date().toISOString());
      if (showLogs) {
        const d = await getSmsLogs(caregiver.username);
        setLogs(d.logs || []);
      }
    } catch (ex) {
      showToast(ex.message || 'Failed to send SMS', 'error');
    } finally { setSending(false); }
  };

  const handleViewLogs = async () => {
    setShowLogs(v => !v);
    if (!showLogs) {
      try { const d = await getSmsLogs(caregiver.username); setLogs(d.logs || []); }
      catch { setLogs([]); }
    }
  };

  const fmtDate = iso => iso ? new Date(iso).toLocaleString() : '—';
  const statusChip = s => {
    if (!s) return null;
    const color = s.includes('fail') ? 'var(--danger)' : s === 'delivered' || s.includes('ok') ? 'var(--success)' : 'var(--gold)';
    return <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
      background: `${color}22`, color, borderRadius: 20, padding: '2px 9px' }}>{s}</span>;
  };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
         onClick={e => e.target === e.currentTarget && handleCloseAttempt()}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 600,
                    maxHeight: '90vh', overflowY: 'auto', position: 'relative',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, var(--navy), #243a5e)', color: 'white',
                      padding: '18px 24px', borderRadius: '14px 14px 0 0',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 17 }}>SMS Reminder Settings</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 2 }}>
              {caregiver.firstName} {caregiver.lastName} · {caregiver.email}
            </div>
          </div>
          <button onClick={handleCloseAttempt}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                     borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>
            ✕ Close
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : (
          <form onSubmit={handleSave} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Last status */}
            {lastSmsSent && (
              <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 8,
                            padding: '10px 14px', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                <span><span style={{ color: 'var(--muted)', fontWeight: 600 }}>Last SMS:</span> {fmtDate(lastSmsSent)}</span>
                <span><span style={{ color: 'var(--muted)', fontWeight: 600 }}>Status:</span> {statusChip(lastSmsStatus)}</span>
              </div>
            )}

            {/* Enable toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '12px 16px',
                            background: form.smsEnabled ? '#f0faf4' : 'var(--cream)',
                            border: `1.5px solid ${form.smsEnabled ? 'var(--success)' : 'var(--border)'}`,
                            borderRadius: 8 }}>
              <input type="checkbox" checked={form.smsEnabled}
                onChange={e => set('smsEnabled', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--teal)', cursor: 'pointer' }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Enable SMS Reminders</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                  When enabled, a daily reminder is sent at the configured time until the Progress Note is submitted.
                </div>
              </div>
            </label>

            {/* Phone */}
            <div className="form-group-card">
              <label className="form-label">Phone Number *</label>
              <input className={`form-input${phoneErr(form.phoneNumber) ? ' error' : ''}`}
                type="tel" placeholder="+12485551234" value={form.phoneNumber}
                onChange={e => set('phoneNumber', e.target.value)}
                onBlur={() => handlePhoneBlur('phoneNumber')} />
              {phoneErr(form.phoneNumber) && (
                <span style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2, display: 'block' }}>
                  {phoneErr(form.phoneNumber)}
                </span>
              )}
              <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'block', lineHeight: 1.5 }}>
                By adding this number, the employee agrees to receive work-related SMS reminders
                from Home Care Associates. Enter in E.164 format (e.g. +12485551234).
              </span>
            </div>

            {/* Timezone */}
            <div className="form-group-card">
              <label className="form-label">Timezone</label>
              <select className="form-input" value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                {SMS_TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>

            {/* Reminder schedule */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group-card">
                <label className="form-label">Start reminders from</label>
                <select className="form-input" value={form.reminderDay}
                  onChange={e => set('reminderDay', parseInt(e.target.value))}>
                  {SMS_DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'block' }}>
                  Daily reminders begin this day each week.
                </span>
              </div>
              <div className="form-group-card">
                <label className="form-label">Daily reminder time</label>
                <input className="form-input" type="time" value={form.reminderTime}
                  onChange={e => set('reminderTime', e.target.value)} />
                <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'block' }}>
                  Sent once per day at this time until note is submitted.
                </span>
              </div>
            </div>

            {/* Max per week */}
            <div className="form-group-card">
              <label className="form-label">Max reminders per week</label>
              <input className="form-input" type="number" min="1" max="7"
                value={form.maxRemindersPerWeek}
                onChange={e => set('maxRemindersPerWeek', parseInt(e.target.value) || 7)} />
              <span style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, display: 'block' }}>
                Safety cap — reminders stop once this limit is reached even if the note is still pending.
              </span>
            </div>

            {/* Escalation */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                              marginBottom: form.escalationEnabled ? 14 : 0 }}>
                <input type="checkbox" checked={form.escalationEnabled}
                  onChange={e => set('escalationEnabled', e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--teal)' }} />
                <div>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>Escalate to admin</span>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    If the caregiver reaches the weekly limit with the note still pending, alert an admin.
                  </div>
                </div>
              </label>
              {form.escalationEnabled && (
                <div className="form-group-card">
                  <label className="form-label">Admin Phone Number</label>
                  <input className={`form-input${phoneErr(form.adminPhoneNumber) ? ' error' : ''}`}
                    type="tel" placeholder="+15865550000" value={form.adminPhoneNumber}
                    onChange={e => set('adminPhoneNumber', e.target.value)}
                    onBlur={() => handlePhoneBlur('adminPhoneNumber')} />
                  {phoneErr(form.adminPhoneNumber) && (
                    <span style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2, display: 'block' }}>
                      {phoneErr(form.adminPhoneNumber)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Message Template */}
            <div className="form-group-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Message Template</label>
                <button type="button"
                  style={{ fontSize: 11, color: 'var(--teal)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 }}
                  onClick={() => set('messageTemplate', DEFAULT_SMS_TEMPLATE)}>
                  Reset to default
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>Insert:</span>
                {['{name}', '{week}', '{url}'].map(ph => (
                  <button key={ph} type="button" onClick={() => insertPlaceholder(ph)}
                    style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20,
                             border: '1px solid var(--teal)', color: 'var(--teal)',
                             background: '#f0faf4', cursor: 'pointer', fontFamily: 'monospace' }}>
                    + {ph}
                  </button>
                ))}
              </div>
              <textarea ref={templateRef} className="form-input" rows={3}
                value={form.messageTemplate}
                onChange={e => set('messageTemplate', e.target.value)}
                style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ fontSize: 11, textAlign: 'right', marginTop: 4,
                            color: form.messageTemplate.length > 160 ? 'var(--danger)' : 'var(--muted)' }}>
                {form.messageTemplate.length} / 160 characters
                {form.messageTemplate.length > 160 && ' — will send as multi-part SMS'}
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 6 }}>
                  Preview
                </div>
                <div style={{ background: '#f8f9fc', border: '1px solid var(--border)',
                              borderRadius: 8, padding: '10px 14px', fontSize: 13,
                              color: '#555', lineHeight: 1.6, fontStyle: 'italic' }}>
                  {renderPreview(form.messageTemplate, caregiver)}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleViewLogs}>
                {showLogs ? 'Hide Logs' : '📋 View SMS Logs'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={handleTest} disabled={testing || sending || !form.phoneNumber}>
                {testing ? 'Sending…' : '📱 Send Test SMS'}
              </button>
              <button type="button" className="btn btn-sm"
                style={{ background: '#eaf4ff', color: '#1a6bbf', border: '1px solid #90c4f0' }}
                onClick={handleSend} disabled={sending || testing || !form.phoneNumber}>
                {sending ? 'Sending…' : '✉ Send SMS'}
              </button>
              <button type="submit" className="btn btn-teal btn-sm" disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Settings'}
              </button>
            </div>

            {/* Logs */}
            {showLogs && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                              letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 8 }}>
                  Recent SMS Activity
                </div>
                {logs.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '12px 0', fontSize: 13 }}>
                    No SMS activity yet.
                  </div>
                ) : (
                  <table className="notes-table" style={{ fontSize: 12 }}>
                    <thead><tr><th>Sent At</th><th>Type</th><th>Week</th><th>Status</th><th>Message</th></tr></thead>
                    <tbody>
                      {logs.map((log, i) => (
                        <tr key={i}>
                          <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmtDate(log.sentAt)}</td>
                          <td style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{log.messageType || 'reminder'}</td>
                          <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{log.weekBeginning || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{statusChip(log.status)}</td>
                          <td style={{ maxWidth: 260, color: 'var(--muted)' }}>
                            <span title={log.messageBody} style={{
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              cursor: log.messageBody ? 'help' : 'default',
                            }}>{log.messageBody || '—'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

          </form>
        )}

        {/* Unsaved-changes confirmation overlay */}
        {showCloseConfirm && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,25,50,0.55)',
                        borderRadius: 14, display: 'flex', alignItems: 'flex-end', zIndex: 10 }}>
            <div style={{ width: '100%', background: '#fff', borderRadius: '0 0 14px 14px',
                          padding: '20px 24px', boxShadow: '0 -6px 30px rgba(0,0,0,0.18)' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>Unsaved changes</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
                You have unsaved changes. Would you like to save before closing?
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => setShowCloseConfirm(false)}>
                  Keep Editing
                </button>
                <button type="button" className="btn btn-sm"
                  style={{ background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                  onClick={() => { setShowCloseConfirm(false); onClose(); }}>
                  Discard Changes
                </button>
                <button type="button" className="btn btn-teal btn-sm"
                  onClick={handleSaveAndClose} disabled={saving}>
                  {saving ? 'Saving…' : 'Save & Close'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}

// ─── User Management Modal ────────────────────────────────────────────────────

// Mirrors the Cognito pool password policy (min 8, upper + lower + digit).
const USER_PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)\S{8,256}$/;
const USER_PASSWORD_RULE = 'At least 8 characters with an uppercase letter, a lowercase letter, and a number';
const USER_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COGNITO_STATUS_HINTS = {
  FORCE_CHANGE_PASSWORD: 'Temporary password pending',
  RESET_REQUIRED: 'Password reset required',
  UNCONFIRMED: 'Email not confirmed',
};

function userDisplayName(u) {
  return `${u.firstName || ''} ${u.lastName || ''}`.trim();
}

function userInitials(u) {
  const first = (u.firstName || '').trim()[0] || '';
  const last = (u.lastName || '').trim()[0] || '';
  const initials = (first + last).toUpperCase();
  return initials || (u.email || '?')[0].toUpperCase();
}

// True when `u` is the signed-in admin. The ID token's `sub` matches the Cognito
// username in this pool, but the email is compared too in case that ever changes.
function isCurrentUser(u, me) {
  if (!u || !me) return false;
  if (me.id && (me.id === u.sub || me.id === u.username)) return true;
  return !!me.email && !!u.email && me.email.toLowerCase() === u.email.toLowerCase();
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

function UserModal({ target, currentUser, initialTab = 'profile', onClose, onUpdated, onOpenSms, showToast }) {
  const [tab, setTab] = useState(initialTab);
  const profileFrom = (u) => ({
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    email: u.email || '',
    role: u.role === 'admin' ? 'admin' : 'caregiver',
  });
  const [form, setForm] = useState(() => profileFrom(target));
  const savedRef = useRef(profileFrom(target));
  const [saving, setSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const [resetMode, setResetMode] = useState('temporary');
  const [newPassword, setNewPassword] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [copiedTemp, setCopiedTemp] = useState(false);

  const [confirmStatus, setConfirmStatus] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const self = isCurrentUser(target, currentUser);
  const isActive = target.status === 'active';
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const isDirty = JSON.stringify(form) !== JSON.stringify(savedRef.current);
  const statusHint = COGNITO_STATUS_HINTS[target.userStatus];

  const handleCloseAttempt = () => {
    if (isDirty) setShowCloseConfirm(true);
    else onClose();
  };

  const validateProfile = () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim().toLowerCase();
    if (!firstName || !lastName) return { error: 'First and last name are required' };
    if (firstName.length > 60 || lastName.length > 60) return { error: 'Names must be 60 characters or fewer' };
    if (!USER_EMAIL_RE.test(email)) return { error: 'Enter a valid email address' };
    return { data: { firstName, lastName, email, role: form.role } };
  };

  const saveProfile = async () => {
    const { error, data } = validateProfile();
    if (error) { showToast(error, 'error'); return false; }
    setSaving(true);
    try {
      const res = await updateUser(target.username, data);
      const updated = { ...target, ...(res.user || data) };
      savedRef.current = profileFrom(updated);
      setForm(profileFrom(updated));
      onUpdated(updated);
      showToast('User updated', 'success');
      return true;
    } catch (ex) {
      showToast(ex.message || 'Could not update user', 'error');
      return false;
    } finally { setSaving(false); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    await saveProfile();
  };

  const handleSaveAndClose = async () => {
    const ok = await saveProfile();
    if (ok) onClose();
    else setShowCloseConfirm(false);
  };

  const handleResetPassword = async () => {
    if (resetMode === 'permanent' && !USER_PASSWORD_RE.test(newPassword)) {
      showToast(USER_PASSWORD_RULE, 'error');
      return;
    }
    setResetting(true);
    try {
      const res = await resetUserPassword(
        target.username,
        resetMode === 'permanent' ? { mode: 'permanent', password: newPassword } : { mode: 'temporary' },
      );
      if (res.temporaryPassword) {
        setTempPassword(res.temporaryPassword);
        setCopiedTemp(false);
        onUpdated({ ...target, userStatus: 'FORCE_CHANGE_PASSWORD' });
      } else {
        setNewPassword('');
        showToast('Password updated', 'success');
      }
      setConfirmReset(false);
    } catch (ex) {
      showToast(ex.message || 'Could not reset password', 'error');
    } finally { setResetting(false); }
  };

  const handleCopyTemp = async () => {
    const ok = await copyText(tempPassword);
    setCopiedTemp(ok);
    if (!ok) showToast('Could not copy. Select the password and copy it manually.', 'error');
  };

  const handleToggleStatus = async () => {
    setTogglingStatus(true);
    try {
      const res = await setUserStatus(target.username, !isActive);
      onUpdated({ ...target, status: res.status || (isActive ? 'disabled' : 'active') });
      showToast(res.message || 'Account updated', 'success');
      setConfirmStatus(false);
    } catch (ex) {
      showToast(ex.message || 'Could not update account status', 'error');
    } finally { setTogglingStatus(false); }
  };

  const displayName = userDisplayName(target);

  return ReactDOM.createPortal(
    <div className="user-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseAttempt(); }}>
      <div className="user-modal" role="dialog" aria-modal="true" aria-labelledby="user-modal-title">

        {/* Header */}
        <div className="user-modal-header">
          <div className={`user-avatar user-avatar--lg${target.role === 'admin' ? ' user-avatar--admin' : ''}${!isActive ? ' user-avatar--disabled' : ''}`}>
            {userInitials(target)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div id="user-modal-title" className="user-modal-title">
              {displayName || <span style={{ fontStyle: 'italic', opacity: 0.8 }}>No name set</span>}
              {self && <span className="user-you-tag">You</span>}
            </div>
            <div className="user-modal-subtitle">{target.email}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span className={`badge badge-${target.role === 'admin' ? 'admin' : 'caregiver'}`}>{target.role === 'admin' ? 'admin' : 'caregiver'}</span>
              <span className={`status-pill status-pill--${isActive ? 'active' : 'disabled'} status-pill--on-dark`}>{isActive ? 'active' : 'disabled'}</span>
            </div>
          </div>
          <button type="button" onClick={handleCloseAttempt} className="user-modal-close" aria-label="Close">✕</button>
        </div>

        {/* Tabs */}
        <div className="user-modal-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'profile'}
            className={`user-modal-tab${tab === 'profile' ? ' user-modal-tab--active' : ''}`}
            onClick={() => setTab('profile')}>
            Profile{isDirty && <span className="user-modal-tab-dot" title="Unsaved changes" />}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'access'}
            className={`user-modal-tab${tab === 'access' ? ' user-modal-tab--active' : ''}`}
            onClick={() => setTab('access')}>
            Access &amp; Password
          </button>
        </div>

        <div className="user-modal-body">

          {/* ── Profile tab ── */}
          {tab === 'profile' && (
            <form onSubmit={handleSaveProfile} className="user-form">
              <div className="form-grid form-grid-2 user-form-grid">
                <div className="form-group">
                  <label className="form-label" htmlFor="um-first">First name</label>
                  <input id="um-first" className="form-input" value={form.firstName} maxLength={60}
                    onChange={e => set('firstName', e.target.value)} autoComplete="off" required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="um-last">Last name</label>
                  <input id="um-last" className="form-input" value={form.lastName} maxLength={60}
                    onChange={e => set('lastName', e.target.value)} autoComplete="off" required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="um-email">Email (sign-in address)</label>
                <input id="um-email" type="email" className="form-input" value={form.email}
                  onChange={e => set('email', e.target.value)} autoComplete="off" required />
                <span className="user-field-hint">
                  Changing the email changes how this person signs in. The new address is marked verified automatically.
                </span>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="um-role">Role</label>
                <select id="um-role" className="form-input" value={form.role} disabled={self}
                  onChange={e => set('role', e.target.value)}>
                  <option value="caregiver">Caregiver</option>
                  <option value="admin">Admin</option>
                </select>
                <span className="user-field-hint">
                  {self
                    ? 'You cannot change your own role. Ask another admin to do it.'
                    : form.role === 'admin'
                      ? 'Admins can manage clients, notes, users, invite codes, and settings.'
                      : 'Caregivers can only create and view their own progress notes.'}
                </span>
                {form.role !== savedRef.current.role && (
                  <div className="user-inline-note user-inline-note--warn">
                    {form.role === 'admin'
                      ? 'This person will gain full admin access the next time they sign in.'
                      : 'This person will lose admin access the next time they sign in.'}
                  </div>
                )}
              </div>

              <div className="user-form-actions">
                <button type="button" className="btn btn-ghost" onClick={handleCloseAttempt} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-teal" disabled={saving || !isDirty}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}

          {/* ── Access tab ── */}
          {tab === 'access' && (
            <div className="user-form">

              {/* Account status */}
              <div className="user-section">
                <div className="user-section-head">
                  <div>
                    <div className="user-section-title">Account status</div>
                    <p className="user-section-desc">
                      {isActive
                        ? 'This account can sign in.'
                        : 'This account is disabled and cannot sign in.'}
                      {statusHint && ` ${statusHint}.`}
                    </p>
                  </div>
                  <span className={`status-pill status-pill--${isActive ? 'active' : 'disabled'}`}>{isActive ? 'active' : 'disabled'}</span>
                </div>
                {self ? (
                  <div className="user-inline-note">You cannot disable your own account.</div>
                ) : confirmStatus ? (
                  <div className="user-inline-note user-inline-note--warn">
                    <div style={{ marginBottom: 10 }}>
                      {isActive
                        ? `Disable ${displayName || target.email}? They will be signed out everywhere and will not be able to sign in until re-enabled.`
                        : `Re-enable ${displayName || target.email}? They will be able to sign in again with their current password.`}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className={`btn btn-sm ${isActive ? 'btn-danger' : 'btn-teal'}`} onClick={handleToggleStatus} disabled={togglingStatus}>
                        {togglingStatus ? 'Working…' : (isActive ? 'Yes, disable account' : 'Yes, enable account')}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmStatus(false)} disabled={togglingStatus}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button type="button"
                      className={`btn btn-sm ${isActive ? 'btn-ghost user-btn-danger-ghost' : 'btn-teal'}`}
                      onClick={() => setConfirmStatus(true)}>
                      {isActive ? 'Disable account' : 'Enable account'}
                    </button>
                  </div>
                )}
              </div>

              {/* Password reset */}
              <div className="user-section">
                <div className="user-section-title">Reset password</div>
                <p className="user-section-desc">
                  {self
                    ? 'Set a new password for your own account. Other devices stay signed in.'
                    : 'Use this when someone is locked out or has forgotten their password. They will be signed out on every device.'}
                </p>

                {tempPassword ? (
                  <div className="temp-password-box">
                    <div className="form-label" style={{ marginBottom: 6 }}>Temporary password</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <code className="temp-password-value">{tempPassword}</code>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={handleCopyTemp}>
                        {copiedTemp ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className="user-section-desc" style={{ marginTop: 10 }}>
                      Share this with {displayName || 'the user'} securely. It is shown only once.
                      They will be asked to choose a new password the next time they sign in.
                    </p>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
                      onClick={() => { setTempPassword(''); setCopiedTemp(false); }}>
                      Done
                    </button>
                  </div>
                ) : (
                  <>
                    <label className={`user-choice${resetMode === 'temporary' ? ' user-choice--active' : ''}`}>
                      <input type="radio" name="reset-mode" value="temporary" checked={resetMode === 'temporary'}
                        onChange={() => { setResetMode('temporary'); setConfirmReset(false); }} />
                      <div>
                        <div className="user-choice-title">Generate a temporary password <span className="user-choice-tag">Recommended</span></div>
                        <div className="user-choice-desc">A one-time password is shown to you. The user must choose their own password at next sign-in.</div>
                      </div>
                    </label>
                    <label className={`user-choice${resetMode === 'permanent' ? ' user-choice--active' : ''}`}>
                      <input type="radio" name="reset-mode" value="permanent" checked={resetMode === 'permanent'}
                        onChange={() => { setResetMode('permanent'); setConfirmReset(false); }} />
                      <div>
                        <div className="user-choice-title">Set a specific password</div>
                        <div className="user-choice-desc">The password you enter takes effect immediately and the user is not asked to change it.</div>
                      </div>
                    </label>

                    {resetMode === 'permanent' && (
                      <div className="form-group">
                        <label className="form-label" htmlFor="um-new-password">New password</label>
                        <PasswordInput id="um-new-password" className="form-input" value={newPassword}
                          onChange={e => setNewPassword(e.target.value)} autoComplete="new-password"
                          placeholder="Enter a new password" />
                        <span className={`user-field-hint${newPassword && !USER_PASSWORD_RE.test(newPassword) ? ' user-field-hint--error' : ''}`}>
                          {USER_PASSWORD_RULE}
                        </span>
                      </div>
                    )}

                    {confirmReset ? (
                      <div className="user-inline-note user-inline-note--warn">
                        <div style={{ marginBottom: 10 }}>
                          Reset the password for {displayName || target.email}?
                          {!self && ' They will be signed out on every device.'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-teal btn-sm" onClick={handleResetPassword} disabled={resetting}>
                            {resetting ? 'Resetting…' : 'Yes, reset password'}
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmReset(false)} disabled={resetting}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <button type="button" className="btn btn-teal btn-sm"
                          disabled={resetMode === 'permanent' && !USER_PASSWORD_RE.test(newPassword)}
                          onClick={() => setConfirmReset(true)}>
                          🔑 Reset password
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* SMS shortcut */}
              {target.role !== 'admin' && onOpenSms && (
                <div className="user-section user-section--row">
                  <div>
                    <div className="user-section-title">SMS reminders</div>
                    <p className="user-section-desc">Phone number, reminder schedule, and message template.</p>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ color: '#1a6b4a', borderColor: '#1a6b4a' }}
                    onClick={() => onOpenSms(target)}>
                    Open SMS settings
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Unsaved-changes confirm */}
        {showCloseConfirm && (
          <div className="user-modal-confirm">
            <div className="user-modal-confirm-card">
              <div className="user-section-title" style={{ marginBottom: 6 }}>Unsaved changes</div>
              <p className="user-section-desc">You have edits to this profile that have not been saved.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCloseConfirm(false)}>Keep editing</button>
                <button type="button" className="btn btn-ghost btn-sm user-btn-danger-ghost" onClick={onClose}>Discard</button>
                <button type="button" className="btn btn-teal btn-sm" onClick={handleSaveAndClose} disabled={saving}>
                  {saving ? 'Saving…' : 'Save & close'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Creates an account and shows the one-time temporary password the admin hands to the new user.
function CreateUserModal({ onClose, onCreated, showToast }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', role: 'caregiver', passwordMode: 'temporary', password: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { user, passwordMode, temporaryPassword? }
  const customPassword = form.passwordMode === 'custom';
  const passwordOk = !customPassword || USER_PASSWORD_RE.test(form.password);
  const [copied, setCopied] = useState(false);

  const set = (key, value) => { setForm(f => ({ ...f, [key]: value })); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim().toLowerCase();
    if (!firstName || !lastName) { setError('First and last name are required'); return; }
    if (!USER_EMAIL_RE.test(email)) { setError('Enter a valid email address'); return; }
    if (!passwordOk) { setError(USER_PASSWORD_RULE); return; }
    setSaving(true);
    try {
      const payload = { firstName, lastName, email, role: form.role };
      if (customPassword) payload.password = form.password;
      const res = await createUser(payload);
      setResult(res);
      showToast('User created', 'success');
    } catch (err) {
      setError(err.message || 'Could not create the user');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const ok = await copyText(result.temporaryPassword);
    setCopied(ok);
    if (!ok) showToast('Could not copy. Select the password and copy it manually.', 'error');
  };

  // Once the account exists it belongs in the list, whether the admin clicks Done or just closes.
  const handleClose = () => {
    if (result) onCreated(result.user);
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="user-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) handleClose(); }}>
      <div className="user-modal" role="dialog" aria-modal="true" aria-labelledby="create-user-title">

        <div className="user-modal-header">
          <div className={`user-avatar user-avatar--lg${form.role === 'admin' ? ' user-avatar--admin' : ''}`}>
            {result ? userInitials(result.user) : '+'}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div id="create-user-title" className="user-modal-title">
              {result ? userDisplayName(result.user) : 'New user'}
            </div>
            <div className="user-modal-subtitle">
              {result ? result.user.email : 'Create an account and choose how they first sign in'}
            </div>
          </div>
          <button type="button" onClick={handleClose} className="user-modal-close" aria-label="Close" disabled={saving}>✕</button>
        </div>

        <div className="user-modal-body">
          {result ? (
            <div className="user-section">
              <div>
                <div className="user-section-title">Account created</div>
                <p className="user-section-desc">
                  {result.temporaryPassword
                    ? `${userDisplayName(result.user)} can sign in with their email and the temporary password below.`
                    : `${userDisplayName(result.user)} can sign in right away with their email and the password you set.`}
                </p>
              </div>
              {!result.temporaryPassword && (
                <div className="user-inline-note">
                  Share the password with them securely. No email has been sent, and the password is not shown here again.
                </div>
              )}
              {result.temporaryPassword && <div className="temp-password-box">
                <div className="form-label" style={{ marginBottom: 6 }}>Temporary password</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <code className="temp-password-value">{result.temporaryPassword}</code>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={handleCopy}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <p className="user-section-desc" style={{ marginTop: 10 }}>
                  Share this securely. It is shown only once, and no email has been sent.
                  They will be asked to choose their own password the first time they sign in.
                </p>
              </div>}
              <div className="user-form-actions">
                <button type="button" className="btn btn-teal" onClick={handleClose}>Done</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-grid form-grid-2 user-form-grid">
                <div className="form-group">
                  <label className="form-label" htmlFor="cu-first">First name</label>
                  <input id="cu-first" className="form-input" value={form.firstName} maxLength={60}
                    onChange={e => set('firstName', e.target.value)} autoComplete="off" autoFocus required />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="cu-last">Last name</label>
                  <input id="cu-last" className="form-input" value={form.lastName} maxLength={60}
                    onChange={e => set('lastName', e.target.value)} autoComplete="off" required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cu-email">Email (sign-in address)</label>
                <input id="cu-email" type="email" className="form-input" value={form.email}
                  onChange={e => set('email', e.target.value)} autoComplete="off" required />
                <span className="user-field-hint">The address is marked verified, so no confirmation email is needed.</span>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cu-role">Role</label>
                <select id="cu-role" className="form-input" value={form.role} onChange={e => set('role', e.target.value)}>
                  <option value="caregiver">Caregiver</option>
                  <option value="admin">Admin</option>
                </select>
                <span className="user-field-hint">
                  {form.role === 'admin'
                    ? 'Admins can manage clients, notes, users, and settings.'
                    : 'Caregivers can only create and view their own progress notes.'}
                </span>
              </div>

              <div className="form-group">
                <div className="form-label">Password</div>
                <div className="user-choice-stack">
                  <label className={`user-choice${!customPassword ? ' user-choice--active' : ''}`}>
                    <input type="radio" name="cu-password-mode" value="temporary" checked={!customPassword}
                      onChange={() => set('passwordMode', 'temporary')} />
                    <div>
                      <div className="user-choice-title">Generate a temporary password <span className="user-choice-tag">Recommended</span></div>
                      <div className="user-choice-desc">Shown to you once after the account is created. The user picks their own password the first time they sign in.</div>
                    </div>
                  </label>
                  <label className={`user-choice${customPassword ? ' user-choice--active' : ''}`}>
                    <input type="radio" name="cu-password-mode" value="custom" checked={customPassword}
                      onChange={() => set('passwordMode', 'custom')} />
                    <div>
                      <div className="user-choice-title">Set the password now</div>
                      <div className="user-choice-desc">The password you enter works immediately and the user is not asked to change it.</div>
                    </div>
                  </label>
                </div>
                {customPassword && (
                  <div style={{ marginTop: 10 }}>
                    <label className="form-label" htmlFor="cu-password">Password for this user</label>
                    <PasswordInput id="cu-password" className="form-input" value={form.password}
                      onChange={e => set('password', e.target.value)} autoComplete="new-password"
                      placeholder="Enter a password" autoFocus />
                    <span className={`user-field-hint${form.password && !passwordOk ? ' user-field-hint--error' : ''}`}>
                      {USER_PASSWORD_RULE}
                    </span>
                  </div>
                )}
              </div>

              {error && <div className="user-field-hint user-field-hint--error">{error}</div>}

              <div className="user-form-actions">
                <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-teal" disabled={saving || !passwordOk}>
                  {saving ? 'Creating…' : 'Create user'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AdminPage({ section: sectionProp } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const requestedSection = sectionProp || query.get('section') || 'clients';
  // Invite codes live inside the Users section now; old ?section=invite-codes links still land there.
  const section = requestedSection === 'invite-codes' ? 'users' : requestedSection;
  const usersView = requestedSection === 'invite-codes' || query.get('view') === 'invites' ? 'invites' : 'accounts';
  const setUsersView = (view) => navigate(`/users${view === 'invites' ? '?view=invites' : ''}`, { replace: true });
  const meta = SECTION_META[section] || SECTION_META['clients'];

  const [caregivers, setCaregivers] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [allNotes, setAllNotes] = useState([]);
  const [newCodes, setNewCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [genForm, setGenForm] = useState({ count: 1, role: 'caregiver', expiryDays: 7, assignedTo: '' });
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(null);
  const [smsModalUser, setSmsModalUser] = useState(null);
  // Users section: which account is open in the management modal, plus list filters
  const [userModal, setUserModal] = useState(null); // { username, tab }
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null); // username awaiting confirmation
  const [deletingUser, setDeletingUser] = useState(null); // username being deleted
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');     // all | admin | caregiver
  const [userStatusFilter, setUserStatusFilter] = useState('all'); // all | active | disabled
  const [selectedCaregiver, setSelectedCaregiver] = useState('');
  const [creatingNote, setCreatingNote] = useState(false);
  const [clients, setClients] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientForm, setClientForm] = useState({ clientName: '', dob: '', medicaidId: '', assignedCaregivers: [], serviceStatus: 'active' });
  const [savingClient, setSavingClient] = useState(false);
  const [filters, setFilters] = useState({
    caregiver: '',
    timeframe: 'all',
    dateFrom: '',
    dateTo: '',
    month: '',
    year: new Date().getFullYear().toString(),
  });
  const [colFilters, setColFilters] = useState({ clientName: '', caregiver: '', weekFrom: '', weekTo: '', status: '' });
  const [globalSearch, setGlobalSearch] = useState('');
  const [sortCol, setSortCol] = useState('savedAt');
  const [sortDir, setSortDir] = useState('desc');

  // Assessment & Agent state
  const FREQ_OPTIONS = ['7 days per week', '6 days per week', '5 days per week', '4 days per week', '3 days per week', '2 days per week', '1 day per week', 'Twice per month', 'Once per month'];
  const DEFAULT_TASK = { task: '', timePerDay: '00:00', frequency: '7 days per week', timePerMonth: '00:00' };
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentClientId, setAssessmentClientId] = useState(null);
  const [assessmentTasks, setAssessmentTasks] = useState([{ ...DEFAULT_TASK }]);
  const [assessmentTotal, setAssessmentTotal] = useState('');
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const EMPTY_ROW = () => ({ clientId: '', weekBegin: '', assignedTo: '', status: 'idle', noteId: null, error: null });
  const [agentRows, setAgentRows] = useState([EMPTY_ROW()]);
  const [agentRunning, setAgentRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  const batchPollRef = useRef(null);
  const [parsingFile, setParsingFile] = useState(false);
  const [parseError, setParseError] = useState('');

  // T&A Report storage state
  const [taModal, setTaModal] = useState({ open: false, clientId: null, clientName: '' });
  const [taReports, setTaReports] = useState([]);
  const [loadingTaReports, setLoadingTaReports] = useState(false);
  const [uploadingTaReport, setUploadingTaReport] = useState(false);
  const [taReportError, setTaReportError] = useState('');

  // Prior Authorization state
  const [priorAuthModal, setPriorAuthModal] = useState({ open: false, clientId: null, clientName: '' });
  const [priorAuths, setPriorAuths] = useState([]);
  const [loadingPriorAuths, setLoadingPriorAuths] = useState(false);
  const [uploadingPriorAuth, setUploadingPriorAuth] = useState(false);
  const [priorAuthError, setPriorAuthError] = useState('');
  const [priorAuthReview, setPriorAuthReview] = useState(null);
  const [savingPriorAuth, setSavingPriorAuth] = useState(false);
  const [expiringAuths, setExpiringAuths] = useState([]);
  const [loadingExpiringAuths, setLoadingExpiringAuths] = useState(false);
  const [expiringAuthsError, setExpiringAuthsError] = useState('');

  // Auto-schedule state
  const [schedule, setSchedule] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [newScheduleRow, setNewScheduleRow] = useState({ clientId: '', caregiverId: '' });
  const [scheduleRunning, setScheduleRunning] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);

  // EVV Agent state
  const [evvForm, setEvvForm] = useState({ clientId: '', weekBegin: '', assignedTo: '' });
  const [evvVisits, setEvvVisits] = useState(null);
  const [evvMappedTimes, setEvvMappedTimes] = useState(null);
  const [evvParsing, setEvvParsing] = useState(false);
  const [evvRunning, setEvvRunning] = useState(false);
  const [evvParseError, setEvvParseError] = useState('');
  const [evvFileName, setEvvFileName] = useState('');
  const [evvClientReports, setEvvClientReports] = useState([]);
  const [evvSelectedReportId, setEvvSelectedReportId] = useState('');
  const [evvLoadingReports, setEvvLoadingReports] = useState(false);
  const [evvDetectedCaregivers, setEvvDetectedCaregivers] = useState([]);
  const [evvCaregiverMappings, setEvvCaregiverMappings] = useState([]);
  const [evvMultiResults, setEvvMultiResults] = useState(null);
  const [evvSelectedCaregiverUsernames, setEvvSelectedCaregiverUsernames] = useState([]);
  const [evvInviteCodes, setEvvInviteCodes] = useState({});

  useEffect(() => {
    const anyOpen = taModal.open || showAssessmentModal || priorAuthModal.open || !!userModal || createUserOpen;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [taModal.open, showAssessmentModal, priorAuthModal.open, userModal, createUserOpen]);

  useEffect(() => {
    if (!user?.isAdmin) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    if (section === 'ai-agent') {
      getSchedule().then(d => setSchedule(d.schedule || [])).catch(() => {});
    }
    if (section === 'prior-auths') {
      loadExpiringAuths();
    }
  }, [section]);

  const fetchAll = () => {
    if (!user?.isAdmin) return;
    Promise.all([
      getCaregivers().then(d => setCaregivers(d.users || [])),
      getInviteCodes().then(d => setInviteCodes(d.codes || [])),
      getNotes().then(d => setAllNotes(d.notes || [])),
      getAdminClients().then(d => setClients(d.clients || [])),
    ]).then(() => setLastUpdated(new Date())).catch(err => showToast(err.message || 'Could not load admin data', 'error')).finally(() => setLoading(false));
  };

  useAutoRefresh(fetchAll);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true); setNewCodes([]);
    try {
      const res = await generateInviteCodes(genForm);
      setNewCodes(res.codes || []);
      const updated = await getInviteCodes();
      setInviteCodes(updated.codes || []);
      showToast(`${res.codes.length} invite code(s) generated!`, 'success');
      setGenForm(f => ({ ...f, assignedTo: '' }));
    } catch (e) {
      showToast('Could not generate codes', 'error');
    } finally { setGenerating(false); }
  };

  const handleDeleteNote = async (id, clientName) => {
    if (!window.confirm(`Delete notes for ${clientName || 'this client'}?`)) return;
    try {
      await deleteNote(id);
      setAllNotes(prev => prev.filter(n => n.id !== id));
      showToast('Note deleted', 'success');
    } catch { showToast('Could not delete note', 'error'); }
  };

  const handleCreateNote = async () => {
    if (!selectedCaregiver) { showToast('Please select a caregiver first', 'error'); return; }
    setCreatingNote(true);
    try {
      const cg = caregivers.find(c => c.username === selectedCaregiver);
      const assignedToName = cg ? `${cg.firstName} ${cg.lastName}` : '';
      const res = await saveNote({
        assignedTo: selectedCaregiver,
        assignedToName,
        caregiver: assignedToName,
        createdBy: 'admin',
        status: 'draft',
      });
      showToast('Note created! Redirecting to form…', 'success');
      setTimeout(() => navigate(`/notes/${res.id}?assignedTo=${selectedCaregiver}&assignedToName=${encodeURIComponent(assignedToName)}`), 1000);
    } catch {
      showToast('Could not create note', 'error');
    } finally { setCreatingNote(false); }
  };

  const resetClientForm = () => {
    setClientForm({ clientName: '', dob: '', medicaidId: '', assignedCaregivers: [], serviceStatus: 'active' });
    setEditingClientId(null);
    setShowClientForm(false);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    if (!clientForm.clientName?.trim()) { showToast('Client name is required', 'error'); return; }
    setSavingClient(true);
    try {
      if (editingClientId) {
        await updateClient(editingClientId, clientForm);
        setClients(prev => prev.map(c => c.clientId === editingClientId ? { ...c, ...clientForm } : c));
        showToast('Client updated!', 'success');
      } else {
        const r = await createClient(clientForm);
        setClients(prev => [...prev, { ...clientForm, clientId: r.clientId }].sort((a, b) => a.clientName.localeCompare(b.clientName)));
        showToast('Client added!', 'success');
      }
      resetClientForm();
    } catch { showToast('Could not save client', 'error'); }
    finally { setSavingClient(false); }
  };

  const handleEditClient = (client) => {
    setClientForm({ clientName: client.clientName, dob: client.dob || '', medicaidId: client.medicaidId || '', assignedCaregivers: client.assignedCaregivers || [], serviceStatus: client.serviceStatus || 'active' });
    setEditingClientId(client.clientId);
    setShowClientForm(true);
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Delete client "${clientName}"? This cannot be undone.`)) return;
    try {
      await deleteClient(clientId);
      setClients(prev => prev.filter(c => c.clientId !== clientId));
      showToast('Client deleted', 'success');
    } catch { showToast('Could not delete client', 'error'); }
  };

  const toggleCaregiverAssignment = (username) => {
    setClientForm(f => ({
      ...f,
      assignedCaregivers: f.assignedCaregivers.includes(username)
        ? f.assignedCaregivers.filter(u => u !== username)
        : [...f.assignedCaregivers, username],
    }));
  };

  const openAssessmentModal = async (clientId) => {
    setAssessmentClientId(clientId);
    setAssessmentTasks([{ ...DEFAULT_TASK }]);
    setAssessmentTotal('');
    setParseError('');
    setShowAssessmentModal(true);
    setLoadingAssessment(true);
    try {
      const data = await getAssessment(clientId);
      setAssessmentTasks(data.tasks?.length ? data.tasks : [{ ...DEFAULT_TASK }]);
      setAssessmentTotal(data.totalMonthly || '');
    } catch (e) {
      if (!e.message?.includes('No assessment')) showToast('Could not load assessment', 'error');
    } finally { setLoadingAssessment(false); }
  };

  const handleSaveAssessment = async (e) => {
    e.preventDefault();
    const validTasks = assessmentTasks.filter(t => t.task.trim() && t.frequency && t.timePerDay);
    if (validTasks.length === 0) { showToast('Add at least one task', 'error'); return; }
    if (!assessmentTotal.trim()) { showToast('Total monthly time is required', 'error'); return; }
    setSavingAssessment(true);
    try {
      await saveAssessment(assessmentClientId, { tasks: validTasks, totalMonthly: assessmentTotal });
      showToast('Assessment saved!', 'success');
      setShowAssessmentModal(false);
    } catch { showToast('Could not save assessment', 'error'); }
    finally { setSavingAssessment(false); }
  };

  const addAssessmentTask = () => setAssessmentTasks(t => [...t, { ...DEFAULT_TASK }]);
  const removeAssessmentTask = (i) => setAssessmentTasks(t => t.filter((_, idx) => idx !== i));
  const updateAssessmentTask = (i, field, value) =>
    setAssessmentTasks(t => t.map((row, idx) => idx === i ? { ...row, [field]: value } : row));

  const handleAssessmentFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';   // reset input so same file can be re-uploaded if needed

    const SUPPORTED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!SUPPORTED.includes(file.type)) {
      setParseError('Unsupported file type. Please upload a JPEG, PNG, WebP, or PDF.');
      return;
    }
    if (file.size > 7 * 1024 * 1024) {
      setParseError('File is too large (max 7 MB). Try a compressed image or a smaller PDF.');
      return;
    }

    setParsingFile(true);
    setParseError('');

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]); // strip data URL prefix
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await parseAssessmentFile(base64, file.type);
      setAssessmentTasks(result.tasks?.length ? result.tasks : [{ ...DEFAULT_TASK }]);
      setAssessmentTotal(result.totalMonthly || '');
      showToast(`Extracted ${result.tasks?.length || 0} tasks from ${file.name}`, 'success');
    } catch (err) {
      setParseError(err.message || 'Could not analyze the file. Try a clearer image or enter data manually.');
    } finally {
      setParsingFile(false);
    }
  };

  const handleAgentGenerate = async (e) => {
    e.preventDefault();
    for (let i = 0; i < agentRows.length; i++) {
      const r = agentRows[i];
      if (!r.clientId)  { showToast(`Row ${i + 1}: select a client`, 'error'); return; }
      if (!r.weekBegin) { showToast(`Row ${i + 1}: select a week`, 'error'); return; }
      if (!r.assignedTo){ showToast(`Row ${i + 1}: select a caregiver`, 'error'); return; }
    }

    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const jobs = agentRows.map(r => {
      const cg = caregivers.find(c => c.username === r.assignedTo);
      return { clientId: r.clientId, weekBegin: r.weekBegin, assignedTo: r.assignedTo, assignedToName: cg ? `${cg.firstName} ${cg.lastName}` : '' };
    });

    setAgentRunning(true);
    setAgentRows(rows => rows.map(r => ({ ...r, status: 'queued', noteId: null, error: null })));
    setBatchProgress({ status: 'running', total: jobs.length, succeeded: 0, failed: 0, jobs: [] });

    // Poll for per-row live status while Lambda processes
    batchPollRef.current = setInterval(async () => {
      try {
        const s = await getBatchStatus(batchId);
        setBatchProgress(s);
        setAgentRows(rows => rows.map((row, i) => {
          const j = s.jobs?.[i];
          return j ? { ...row, status: j.status, noteId: j.noteId || null, error: j.error || null } : row;
        }));
      } catch {}
    }, 2000);

    try {
      const result = await agentBatchGenerateNotes({ batchId, jobs });
      clearInterval(batchPollRef.current);

      setBatchProgress({ ...result, status: 'complete' });
      setAgentRows(rows => rows.map((row, i) => {
        const j = result.succeeded?.find((_, si) => si === i - result.failed?.length) || null;
        const failed = result.failed?.find(f => f.clientId === row.clientId && f.weekBegin === row.weekBegin);
        return { ...row, status: failed ? 'failed' : 'done', error: failed?.error || null };
      }));

      const { succeeded = [], failed = [] } = result.summary || {};
      if (succeeded > 0) showToast(`${succeeded} note${succeeded !== 1 ? 's' : ''} generated successfully!`, 'success');
      if (failed > 0)    showToast(`${failed} job${failed !== 1 ? 's' : ''} failed — check individual rows`, 'error');

      const updated = await getNotes();
      setAllNotes(updated.notes || []);

      // Reset after 4 s so the user can see the final status before it clears
      setTimeout(() => {
        setAgentRows([EMPTY_ROW()]);
        setBatchProgress(null);
        setAgentRunning(false);
      }, 4000);

    } catch (err) {
      clearInterval(batchPollRef.current);
      showToast(err.message || 'Batch generation failed', 'error');
      setAgentRows(rows => rows.map(r => ({ ...r, status: 'idle' })));
      setBatchProgress(null);
      setAgentRunning(false);
    }
  };

  const handleAddScheduleRow = async () => {
    const { clientId, caregiverId } = newScheduleRow;
    if (!clientId)    { showToast('Select a client', 'error'); return; }
    if (!caregiverId) { showToast('Select a caregiver', 'error'); return; }
    if (schedule.some(s => s.clientId === clientId)) {
      showToast('This client is already scheduled', 'error'); return;
    }
    const client    = clients.find(c => c.clientId === clientId);
    const caregiver = caregivers.find(c => c.username === caregiverId);
    const entry = {
      clientId,
      caregiverId,
      clientName:    client    ? client.clientName                              : '',
      caregiverName: caregiver ? `${caregiver.firstName} ${caregiver.lastName}` : '',
      enabled: true,
    };
    try {
      await upsertSchedule(entry);
      setSchedule(prev => [...prev, entry]);
      setNewScheduleRow({ clientId: '', caregiverId: '' });
      showToast('Added to auto-schedule', 'success');
    } catch { showToast('Could not save schedule entry', 'error'); }
  };

  const handleRemoveScheduleRow = async (clientId, clientName) => {
    if (!window.confirm(`Remove ${clientName || clientId} from auto-schedule?`)) return;
    try {
      await deleteSchedule(clientId);
      setSchedule(prev => prev.filter(s => s.clientId !== clientId));
      showToast('Removed from auto-schedule', 'success');
    } catch { showToast('Could not remove schedule entry', 'error'); }
  };

  const handleToggleScheduleEnabled = async (entry) => {
    const updated = { ...entry, enabled: !entry.enabled };
    try {
      await upsertSchedule(updated);
      setSchedule(prev => prev.map(s => s.clientId === entry.clientId ? updated : s));
    } catch { showToast('Could not update schedule entry', 'error'); }
  };

  const handleRunScheduleNow = async () => {
    if (!window.confirm('Generate notes for all enrolled clients for the current week right now?')) return;
    setScheduleRunning(true);
    setScheduleResult(null);
    try {
      const result = await runScheduleNow();
      setScheduleResult(result);
      if (result.generated > 0) {
        showToast(`Generated ${result.generated} note${result.generated !== 1 ? 's' : ''}`, 'success');
        const updated = await getNotes();
        setAllNotes(updated.notes || []);
      } else {
        showToast('No new notes to generate (all up to date)', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Run failed', 'error');
    } finally { setScheduleRunning(false); }
  };

  const openTaModal = async (clientId, clientName) => {
    setTaModal({ open: true, clientId, clientName });
    setTaReportError('');
    setLoadingTaReports(true);
    try {
      const data = await listTaReports(clientId);
      setTaReports(data.reports || []);
    } catch { setTaReportError('Could not load reports'); }
    finally { setLoadingTaReports(false); }
  };

  const handleTaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const ok = file.type.includes('csv') || file.type.includes('text/plain') ||
                file.type.includes('spreadsheet') || file.type.includes('ms-excel') ||
                /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!ok) { setTaReportError('Upload a CSV or Excel (.xlsx) file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setTaReportError('File too large (max 10 MB).'); return; }

    setUploadingTaReport(true); setTaReportError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await uploadTaReport(taModal.clientId, { fileData: base64, fileName: file.name, mimeType: file.type });
      setTaReports(prev => [{ reportId: result.reportId, fileName: result.fileName, uploadedAt: result.uploadedAt }, ...prev]);
      showToast(`Uploaded: ${file.name}`, 'success');
    } catch (err) {
      setTaReportError(err.message || 'Upload failed');
    } finally { setUploadingTaReport(false); }
  };

  const handleTaDelete = async (reportId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    try {
      await deleteTaReport(taModal.clientId, reportId);
      setTaReports(prev => prev.filter(r => r.reportId !== reportId));
      showToast('Report deleted', 'success');
    } catch { showToast('Could not delete report', 'error'); }
  };

  const openPriorAuthModal = async (clientId, clientName) => {
    setPriorAuthModal({ open: true, clientId, clientName });
    setPriorAuthError('');
    setPriorAuthReview(null);
    setLoadingPriorAuths(true);
    try {
      const data = await listPriorAuths(clientId);
      setPriorAuths(data.authorizations || []);
    } catch { setPriorAuthError('Could not load authorizations'); }
    finally { setLoadingPriorAuths(false); }
  };

  const handlePriorAuthUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.type !== 'application/pdf') { setPriorAuthError('Upload a PDF file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setPriorAuthError('File too large (max 10 MB).'); return; }

    setUploadingPriorAuth(true); setPriorAuthError('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await uploadPriorAuth(priorAuthModal.clientId, { fileData: base64, fileName: file.name, mimeType: file.type });
      setPriorAuthReview(result);
      showToast(`Extracted data from ${file.name} — review and confirm below`, 'success');
    } catch (err) {
      setPriorAuthError(err.message || 'Upload failed');
    } finally { setUploadingPriorAuth(false); }
  };

  const updatePriorAuthReview = (field, value) =>
    setPriorAuthReview(r => ({ ...r, [field]: value }));

  const handleConfirmPriorAuth = async () => {
    if (!priorAuthReview) return;
    const { authId, authNumber, payer, serviceType, unitsAuthorized, unitsPeriod, unitsPerWeek, startDate, endDate, memberId, diagnosisCode, caseManagerName, caseManagerPhone } = priorAuthReview;
    if (!authNumber?.trim()) { showToast('Authorization number is required', 'error'); return; }
    if (!startDate || !endDate) { showToast('Start and end dates are required', 'error'); return; }
    if (unitsPerWeek !== '' && unitsPerWeek != null && unitsToHours(unitsPerWeek) === null) {
      showToast('Units Approved per Week must be a non-negative number', 'error');
      return;
    }
    setSavingPriorAuth(true);
    try {
      await confirmPriorAuth(priorAuthModal.clientId, authId, { authNumber, payer, serviceType, unitsAuthorized, unitsPeriod, unitsPerWeek, startDate, endDate, memberId, diagnosisCode, caseManagerName, caseManagerPhone });
      showToast('Prior Authorization confirmed', 'success');
      // Copy the document's care details onto the client record where it is still blank.
      const client = clients.find(c => c.clientId === priorAuthModal.clientId);
      if (client) {
        try {
          const updated = await fillBlankCareDetails(client, { memberId, diagnosisCode, caseManagerName, caseManagerPhone });
          if (updated !== client) setClients(prev => prev.map(c => (c.clientId === updated.clientId ? updated : c)));
        } catch (e) { showToast(e.message || 'Authorization saved, but the client care details could not be updated', 'error'); }
      }
      setPriorAuthReview(null);
      const data = await listPriorAuths(priorAuthModal.clientId);
      setPriorAuths(data.authorizations || []);
    } catch (err) {
      showToast(err.message || 'Could not confirm authorization', 'error');
    } finally { setSavingPriorAuth(false); }
  };

  const handleDeletePriorAuth = async (authId, authNumber) => {
    if (!window.confirm(`Delete authorization "${authNumber || authId}"? This cannot be undone.`)) return;
    try {
      await deletePriorAuth(priorAuthModal.clientId, authId);
      setPriorAuths(prev => prev.filter(a => a.authId !== authId));
      showToast('Authorization deleted', 'success');
    } catch { showToast('Could not delete authorization', 'error'); }
  };

  const handleSnoozePriorAuth = async (clientId, authId, days) => {
    try {
      const result = await snoozePriorAuth(clientId, authId, days);
      setPriorAuths(prev => prev.map(a => a.authId === authId ? { ...a, snoozedUntil: result.snoozedUntil } : a));
      setExpiringAuths(prev => prev.map(a => a.authId === authId ? { ...a, snoozedUntil: result.snoozedUntil } : a));
      showToast(`Snoozed for ${days} day${days !== 1 ? 's' : ''}`, 'success');
    } catch (err) { showToast(err.message || 'Could not snooze', 'error'); }
  };

  const loadExpiringAuths = async () => {
    setLoadingExpiringAuths(true);
    setExpiringAuthsError('');
    try {
      const data = await getExpiringPriorAuths();
      setExpiringAuths(data.authorizations || []);
    } catch { setExpiringAuthsError('Could not load expiring authorizations'); }
    finally { setLoadingExpiringAuths(false); }
  };

  const DAYS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  const mapVisitsToWeekDays = (visits, weekBegin, caregiverNameFilter = null) => {
    const timeIn  = Object.fromEntries(DAYS.map(d => [d, '']));
    const timeOut = Object.fromEntries(DAYS.map(d => [d, '']));
    if (!weekBegin || !visits.length) return { timeIn, timeOut };
    const weekStart = new Date(weekBegin + 'T00:00:00');
    const filtered = caregiverNameFilter
      ? visits.filter(v => v.caregiverName === caregiverNameFilter)
      : visits;
    for (const v of filtered) {
      const diff = Math.round((new Date(v.date + 'T00:00:00') - weekStart) / 86400000);
      if (diff >= 0 && diff <= 6) {
        if (v.visitIn)  timeIn[DAYS[diff]]  = v.visitIn;
        if (v.visitOut) timeOut[DAYS[diff]] = v.visitOut;
      }
    }
    return { timeIn, timeOut };
  };

  const handleEvvFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const ok = file.type.includes('csv') || file.type.includes('text/plain') ||
                file.type.includes('spreadsheet') || file.type.includes('ms-excel') ||
                /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!ok) { setEvvParseError('Unsupported file. Please upload a CSV or Excel (.xlsx) file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setEvvParseError('File too large (max 5 MB).'); return; }

    setEvvParsing(true); setEvvParseError(''); setEvvFileName(file.name);
    setEvvVisits(null); setEvvMappedTimes(null);
    setEvvDetectedCaregivers([]); setEvvCaregiverMappings([]); setEvvMultiResults(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await parseEvvFile(base64, file.name, file.type);
      const visits   = result.visits || [];
      const detected = result.detectedCaregivers || [];
      setEvvVisits(visits);
      setEvvDetectedCaregivers(detected);
      if (detected.length > 1) {
        setEvvCaregiverMappings(detected.map(taName => {
          const autoMatch = caregivers.find(cg => {
            const fn = cg.firstName.toLowerCase();
            const ln = cg.lastName.toLowerCase();
            const tn = taName.toLowerCase();
            return (tn.includes(fn) && tn.includes(ln)) || tn === `${fn} ${ln}` || tn === `${ln} ${fn}`;
          });
          return { taName, assignedTo: autoMatch ? autoMatch.username : '', times: mapVisitsToWeekDays(visits, evvForm.weekBegin, taName) };
        }).filter(m => DAYS.some(d => m.times.timeIn[d])));
        setEvvMappedTimes(null);
      } else {
        setEvvCaregiverMappings([]);
        if (evvForm.weekBegin && visits.length) setEvvMappedTimes(mapVisitsToWeekDays(visits, evvForm.weekBegin));
      }
      showToast(`Parsed ${visits.length} visit record${visits.length !== 1 ? 's' : ''}${detected.length > 1 ? ` · ${detected.length} caregivers detected` : ''} from ${file.name}`, 'success');
    } catch (err) {
      setEvvParseError(err.message || 'Could not parse the file. Check that it contains Date, Visit In, and Visit Out columns.');
    } finally { setEvvParsing(false); }
  };

  const handleEvvClientChange = async (clientId) => {
    const client = clients.find(c => c.clientId === clientId);
    const assignedUsernames = (client?.assignedCaregivers || [])
      .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'));
    const firstUsername = assignedUsernames[0] || '';
    const matchedCg = caregivers.find(cg => cg.username === firstUsername);
    setEvvForm(f => ({ ...f, clientId, assignedTo: matchedCg ? matchedCg.username : f.assignedTo }));
    setEvvSelectedCaregiverUsernames(assignedUsernames);
    setEvvSelectedReportId('');
    setEvvVisits(null); setEvvMappedTimes(null); setEvvFileName('');
    setEvvDetectedCaregivers([]); setEvvCaregiverMappings([]); setEvvMultiResults(null);
    if (!clientId) { setEvvClientReports([]); return; }
    setEvvLoadingReports(true);
    try {
      const data = await listTaReports(clientId);
      setEvvClientReports(data.reports || []);
    } catch { setEvvClientReports([]); }
    finally { setEvvLoadingReports(false); }
  };

  const handleEvvStoredReportSelect = async (reportId) => {
    setEvvSelectedReportId(reportId);
    setEvvVisits(null); setEvvMappedTimes(null); setEvvParseError('');
    setEvvDetectedCaregivers([]); setEvvCaregiverMappings([]); setEvvMultiResults(null);
    // Re-populate checkbox selection from client's assigned caregivers on new report load
    const clientObjForReport = clients.find(c => c.clientId === evvForm.clientId);
    const assignedForReport = (clientObjForReport?.assignedCaregivers || [])
      .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'));
    setEvvSelectedCaregiverUsernames(assignedForReport);
    if (!reportId) return;
    const report = evvClientReports.find(r => r.reportId === reportId);
    setEvvFileName(report?.fileName || '');
    setEvvParsing(true);
    try {
      const result = await parseStoredEvvReport(evvForm.clientId, reportId);
      const visits   = result.visits || [];
      const detected = result.detectedCaregivers || [];
      setEvvVisits(visits);
      setEvvDetectedCaregivers(detected);
      if (detected.length > 1) {
        setEvvCaregiverMappings(detected.map(taName => {
          const autoMatch = caregivers.find(cg => {
            const fn = cg.firstName.toLowerCase();
            const ln = cg.lastName.toLowerCase();
            const tn = taName.toLowerCase();
            return (tn.includes(fn) && tn.includes(ln)) || tn === `${fn} ${ln}` || tn === `${ln} ${fn}`;
          });
          return { taName, assignedTo: autoMatch ? autoMatch.username : '', times: mapVisitsToWeekDays(visits, evvForm.weekBegin, taName) };
        }).filter(m => DAYS.some(d => m.times.timeIn[d])));
        setEvvMappedTimes(null);
      } else {
        setEvvCaregiverMappings([]);
        if (evvForm.weekBegin && visits.length) setEvvMappedTimes(mapVisitsToWeekDays(visits, evvForm.weekBegin));
      }
      showToast(`Loaded ${visits.length} visit record${visits.length !== 1 ? 's' : ''}${detected.length > 1 ? ` · ${detected.length} caregivers detected` : ''} from ${report?.fileName}`, 'success');
    } catch (err) {
      setEvvParseError(err.message || 'Could not parse report.');
    } finally { setEvvParsing(false); }
  };

  const handleEvvWeekChange = (weekBegin) => {
    setEvvForm(f => ({ ...f, weekBegin }));
    if (evvVisits?.length && weekBegin) {
      if (evvDetectedCaregivers.length > 1) {
        setEvvCaregiverMappings(prev => {
          const prevMap = Object.fromEntries(prev.map(m => [m.taName, m.assignedTo]));
          return evvDetectedCaregivers.map(taName => ({
            taName,
            assignedTo: prevMap[taName] || '',
            times: mapVisitsToWeekDays(evvVisits, weekBegin, taName),
          })).filter(m => DAYS.some(d => m.times.timeIn[d]));
        });
      } else {
        setEvvMappedTimes(mapVisitsToWeekDays(evvVisits, weekBegin));
      }
    }
  };

  const handleEvvInvite = async (taName) => {
    setEvvInviteCodes(prev => ({ ...prev, [taName]: { status: 'generating', code: '' } }));
    try {
      const res = await generateInviteCodes({ count: 1, role: 'caregiver', expiryDays: 7, assignedTo: taName });
      const code = res.codes?.[0] || '';
      setEvvInviteCodes(prev => ({ ...prev, [taName]: { status: 'done', code } }));
      setInviteCodes(prev => [...res.codes.map(c => ({ code: c, role: 'caregiver', assignedTo: taName, expired: false })), ...prev]);
    } catch (err) {
      setEvvInviteCodes(prev => ({ ...prev, [taName]: { status: 'error', code: '' } }));
      showToast('Failed to generate invite code', 'error');
    }
  };

  const handleEvvGenerate = async (e) => {
    e.preventDefault();
    if (!evvForm.clientId)  { showToast('Select a client', 'error'); return; }
    if (!evvForm.weekBegin) { showToast('Select a week start date', 'error'); return; }

    if (evvCaregiverMappings.length > 0) {
      // Multi-caregiver mode — skip rows pending new-user registration
      const toGenerate = evvCaregiverMappings.filter(m => m.assignedTo && m.assignedTo !== '__new__');
      const unmatched  = evvCaregiverMappings.filter(m => !m.assignedTo);
      if (unmatched.length) {
        showToast(`Map all caregivers before generating (${unmatched.map(m => m.taName).join(', ')})`, 'error'); return;
      }
      if (!toGenerate.length) {
        showToast('No mapped caregivers to generate notes for', 'error'); return;
      }
      setEvvRunning(true);
      setEvvMultiResults(toGenerate.map(m => ({ taName: m.taName, status: 'generating', noteId: null, error: null })));
      for (let i = 0; i < toGenerate.length; i++) {
        const m  = toGenerate[i];
        const cg = caregivers.find(c => c.username === m.assignedTo);
        try {
          const result = await agentEvvGenerateNote({
            clientId: evvForm.clientId,
            weekBegin: evvForm.weekBegin,
            assignedTo: m.assignedTo,
            assignedToName: cg ? `${cg.firstName} ${cg.lastName}` : m.taName,
            timeIn:  m.times.timeIn,
            timeOut: m.times.timeOut,
          });
          setEvvMultiResults(prev => prev.map((r, ri) => ri === i ? { ...r, status: 'done', noteId: result.noteId } : r));
        } catch (err) {
          setEvvMultiResults(prev => prev.map((r, ri) => ri === i ? { ...r, status: 'error', error: err.message || 'Generation failed' } : r));
        }
      }
      setAllNotes((await getNotes()).notes || []);
      setEvvRunning(false);
    } else {
      const clientObjGen = clients.find(c => c.clientId === evvForm.clientId);
      const assignedCgsGen = (clientObjGen?.assignedCaregivers || [])
        .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'));
      const isCheckboxMultiMode = assignedCgsGen.length > 1;

      if (isCheckboxMultiMode) {
        // Checkbox multi-caregiver mode: per-caregiver T&A times when identifiable, else shared
        if (!evvSelectedCaregiverUsernames.length) { showToast('Select at least one caregiver', 'error'); return; }
        if (!evvMappedTimes) { showToast('Upload a T&A report first', 'error'); return; }
        setEvvRunning(true);
        setEvvMultiResults(evvSelectedCaregiverUsernames.map(u => {
          const cg = caregivers.find(c => c.username === u);
          return { taName: cg ? `${cg.firstName} ${cg.lastName}` : u, status: 'generating', noteId: null, error: null };
        }));
        for (let i = 0; i < evvSelectedCaregiverUsernames.length; i++) {
          const u  = evvSelectedCaregiverUsernames[i];
          const cg = caregivers.find(c => c.username === u);
          const cgFullName = cg ? `${cg.firstName} ${cg.lastName}` : '';
          // If the T&A report has caregiver name data, filter to only that caregiver's visits
          let cgTimes = evvMappedTimes;
          if (evvVisits?.length && cgFullName) {
            const cgSpecificVisits = evvVisits.filter(v => {
              if (!v.caregiverName) return false;
              const vn = v.caregiverName.toLowerCase();
              return vn === cgFullName.toLowerCase() ||
                     vn.includes((cg?.firstName || '').toLowerCase()) ||
                     cgFullName.toLowerCase().includes(vn.split(' ')[0]);
            });
            if (cgSpecificVisits.length > 0) {
              cgTimes = mapVisitsToWeekDays(cgSpecificVisits, evvForm.weekBegin);
            }
          }
          try {
            const result = await agentEvvGenerateNote({
              clientId: evvForm.clientId,
              weekBegin: evvForm.weekBegin,
              assignedTo: u,
              assignedToName: cgFullName,
              timeIn:  cgTimes.timeIn,
              timeOut: cgTimes.timeOut,
            });
            setEvvMultiResults(prev => prev.map((r, ri) => ri === i ? { ...r, status: 'done', noteId: result.noteId } : r));
          } catch (err) {
            setEvvMultiResults(prev => prev.map((r, ri) => ri === i ? { ...r, status: 'error', error: err.message || 'Generation failed' } : r));
          }
        }
        setAllNotes((await getNotes()).notes || []);
        setEvvRunning(false);
      } else {
        // Single caregiver mode
        if (!evvForm.assignedTo){ showToast('Select a caregiver', 'error'); return; }
        if (!evvMappedTimes)    { showToast('Upload a T&A report first', 'error'); return; }
        const cg = caregivers.find(c => c.username === evvForm.assignedTo);
        const assignedToName = cg ? `${cg.firstName} ${cg.lastName}` : '';
        setEvvRunning(true);
        try {
          const result = await agentEvvGenerateNote({ ...evvForm, assignedToName, timeIn: evvMappedTimes.timeIn, timeOut: evvMappedTimes.timeOut });
          showToast(result.message || 'EVV note generated!', 'success');
          setAllNotes((await getNotes()).notes || []);
          setEvvForm({ clientId: '', weekBegin: '', assignedTo: '' });
          setEvvVisits(null); setEvvMappedTimes(null); setEvvFileName('');
          setEvvClientReports([]); setEvvSelectedReportId('');
        } catch (err) {
          showToast(err.message || 'Agent error — check that an assessment exists for this client', 'error');
        } finally { setEvvRunning(false); }
      }
    }
  };

  const handleDelete = async (code, assignedTo) => {
    const label = assignedTo ? `the code for ${assignedTo}` : 'this invite code';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await deleteInviteCode(code);
      setInviteCodes(prev => prev.filter(c => c.code !== code));
      showToast('Invite code deleted', 'success');
    } catch {
      showToast('Could not delete code', 'error');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const copyAll = () => {
    navigator.clipboard.writeText(newCodes.join('\n')).then(() => {
      showToast('All codes copied!', 'success');
    });
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const activeCodes = inviteCodes.filter(c => !c.expired);
  const expiredCodes = inviteCodes.filter(c => c.expired);

  // ── Users section: counts, filtered list, and the account open in the modal ──
  const userCounts = {
    total: caregivers.length,
    admins: caregivers.filter(u => u.role === 'admin').length,
    disabled: caregivers.filter(u => u.status !== 'active').length,
  };
  userCounts.caregivers = userCounts.total - userCounts.admins;
  const userQuery = userSearch.trim().toLowerCase();
  const visibleUsers = caregivers
    .filter(u => userRoleFilter === 'all' || (userRoleFilter === 'admin' ? u.role === 'admin' : u.role !== 'admin'))
    .filter(u => userStatusFilter === 'all' || u.status === userStatusFilter)
    .filter(u => !userQuery || `${u.firstName || ''} ${u.lastName || ''} ${u.email || ''}`.toLowerCase().includes(userQuery))
    .sort((a, b) => {
      const an = userDisplayName(a), bn = userDisplayName(b);
      if (!an && bn) return 1;   // accounts without a name sort last
      if (an && !bn) return -1;
      return an.localeCompare(bn) || (a.email || '').localeCompare(b.email || '');
    });
  const userFiltersActive = !!userQuery || userRoleFilter !== 'all' || userStatusFilter !== 'all';
  // Always read the modal's user from the live list so edits made in the modal show immediately.
  const userModalTarget = userModal ? caregivers.find(u => u.username === userModal.username) : null;
  const applyUserUpdate = (updated) =>
    setCaregivers(prev => prev.map(u => (u.username === updated.username ? { ...u, ...updated } : u)));
  const handleDeleteUser = async (u) => {
    setDeletingUser(u.username);
    try {
      await deleteUser(u.username);
      setCaregivers(prev => prev.filter(x => x.username !== u.username));
      if (userModal?.username === u.username) setUserModal(null);
      showToast(`Deleted ${userDisplayName(u) || u.email}`, 'success');
    } catch (ex) {
      showToast(ex.message || 'Could not delete the user', 'error');
    } finally {
      setDeletingUser(null);
      setConfirmDeleteUser(null);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortArrow = ({ col }) => (
    <span style={{ marginLeft: 4, opacity: sortCol === col ? 1 : 0.3 }}>
      {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const statusBadge = (note) => {
    const s = note.status || 'draft';
    const cfg = {
      draft:               { bg: '#f3f4f6', color: '#6b7280', label: 'Draft' },
      pending_signature:   { bg: '#fef3c7', color: '#92400e', label: 'Pending Sig.' },
      signed:              { bg: '#d1fae5', color: '#065f46', label: 'Signed' },
    }[s] || { bg: '#f3f4f6', color: '#6b7280', label: s };
    return <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
  };

  const hasColFilters = globalSearch || colFilters.clientName || colFilters.caregiver || colFilters.weekFrom || colFilters.weekTo || colFilters.status;

  const clearColFilters = () => { setGlobalSearch(''); setColFilters({ clientName: '', caregiver: '', weekFrom: '', weekTo: '', status: '' }); };

  // Filter notes based on selected filters
  const filteredNotes = allNotes.filter(note => {
    const noteDate = note.weekBegin ? new Date(note.weekBegin + 'T00:00:00') : null;
    const savedDate = note.savedAt ? new Date(note.savedAt) : null;
    const now = new Date();

    // Global search
    if (globalSearch) {
      const q = globalSearch.toLowerCase();
      const hay = `${note.clientName || ''} ${note.caregiverName || ''} ${note.assignedToName || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Column filters
    if (colFilters.clientName && !(note.clientName || '').toLowerCase().includes(colFilters.clientName.toLowerCase())) return false;
    if (colFilters.caregiver && note.userId !== colFilters.caregiver && note.assignedTo !== colFilters.caregiver) return false;
    if (colFilters.weekFrom && note.weekBegin && note.weekBegin < colFilters.weekFrom) return false;
    if (colFilters.weekTo && note.weekBegin && note.weekBegin > colFilters.weekTo) return false;
    if (colFilters.status && (note.status || 'draft') !== colFilters.status) return false;

    // Caregiver filter (legacy)
    if (filters.caregiver && note.userId !== filters.caregiver && note.assignedTo !== filters.caregiver) return false;

    // Timeframe filter
    if (filters.timeframe === 'this_week') {
      const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0,0,0,0);
      if (!noteDate || noteDate < weekStart) return false;
    } else if (filters.timeframe === 'last_week') {
      const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay() - 7);
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7);
      weekStart.setHours(0,0,0,0); weekEnd.setHours(0,0,0,0);
      if (!noteDate || noteDate < weekStart || noteDate >= weekEnd) return false;
    } else if (filters.timeframe === 'this_month') {
      if (!noteDate || noteDate.getMonth() !== now.getMonth() || noteDate.getFullYear() !== now.getFullYear()) return false;
    } else if (filters.timeframe === 'last_month') {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      if (!noteDate || noteDate.getMonth() !== lastMonth.getMonth() || noteDate.getFullYear() !== lastMonth.getFullYear()) return false;
    } else if (filters.timeframe === 'last_30') {
      const cutoff = new Date(now); cutoff.setDate(now.getDate() - 30);
      if (!noteDate || noteDate < cutoff) return false;
    } else if (filters.timeframe === 'last_90') {
      const cutoff = new Date(now); cutoff.setDate(now.getDate() - 90);
      if (!noteDate || noteDate < cutoff) return false;
    } else if (filters.timeframe === 'this_year') {
      if (!noteDate || noteDate.getFullYear() !== now.getFullYear()) return false;
    } else if (filters.timeframe === 'specific_month') {
      if (!filters.month || !filters.year) return true;
      const [yr, mo] = [parseInt(filters.year), parseInt(filters.month) - 1];
      if (!noteDate || noteDate.getMonth() !== mo || noteDate.getFullYear() !== yr) return false;
    } else if (filters.timeframe === 'custom') {
      if (filters.dateFrom && noteDate && noteDate < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && noteDate && noteDate > new Date(filters.dateTo + 'T23:59:59')) return false;
    }
    return true;
  }).sort((a, b) => {
    let av = '', bv = '';
    if (sortCol === 'clientName')  { av = a.clientName || ''; bv = b.clientName || ''; }
    else if (sortCol === 'caregiver') { av = a.assignedToName || a.caregiverName || ''; bv = b.assignedToName || b.caregiverName || ''; }
    else if (sortCol === 'weekBegin') { av = a.weekBegin || ''; bv = b.weekBegin || ''; }
    else if (sortCol === 'savedAt')   { av = a.savedAt || ''; bv = b.savedAt || ''; }
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const formatDate = (str) => {
    if (!str) return '—';
    return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // 1 unit = 15 minutes, so hours = units / 4. Blank stays blank; negative or
  // non-numeric input returns null so callers can flag it instead of guessing.
  const unitsToHours = (units) => {
    if (units === '' || units === null || units === undefined) return '';
    const n = Number(units);
    if (!Number.isFinite(n) || n < 0) return null;
    return (n / 4).toFixed(2);
  };

  // Deep links from the new Clients and Authorizations pages:
  // /clients/manage?open=edit|assessment|ta|prior-auth|new&client=ID
  const openRequest = query.get('open');
  const openClientId = query.get('client');
  const openHandled = useRef(null);
  useEffect(() => {
    if (!openRequest || loading) return;
    const key = `${openRequest}:${openClientId || ''}`;
    if (openHandled.current === key) return;
    if (openRequest === 'new') { openHandled.current = key; resetClientForm(); setShowClientForm(true); return; }
    const client = clients.find(c => c.clientId === openClientId);
    if (!client) return;
    openHandled.current = key;
    if (openRequest === 'edit') handleEditClient(client);
    else if (openRequest === 'assessment') openAssessmentModal(client.clientId);
    else if (openRequest === 'ta') openTaModal(client.clientId, client.clientName);
    else if (openRequest === 'prior-auth') openPriorAuthModal(client.clientId, client.clientName);
  }, [openRequest, openClientId, clients, loading]);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const YEARS = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <>
      <main className="main-content">
        <div className="top-bar">
          <div>
            <div className="page-title">{meta.title}</div>
            <div className="page-subtitle">
              {meta.subtitle}
              {lastUpdated && (
                <span style={{ marginLeft: 12, fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>
                  ↻ {lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── AI AGENT ── */}
        {section === 'ai-agent' && <div className="card">
          <div className="card-header" style={{ background: 'linear-gradient(135deg, #1a2b4a 0%, #2d5a8e 100%)', color: '#fff' }}>
            ✦ AI Agent — Generate Weekly Progress Notes
          </div>
          <div className="card-body">
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
              Add up to 3 clients and generate all their notes at once. The AI fills all 7 days of ADLs/IADLs,
              calculates time in/out, and writes clinical narrative notes automatically. Each note is placed in{' '}
              <strong>Pending Signature</strong> status for the caregiver to review and sign.
            </p>

            <form onSubmit={handleAgentGenerate}>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr 32px', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Week (Saturday)</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Caregiver</span>
                <span />
              </div>

              {/* One row per client */}
              {agentRows.map((row, i) => {
                const statusIcon = { idle: null, queued: '⏳', loading: '⏳', generating: '🔄', saving: '💾', done: '✅', failed: '❌' }[row.status] || null;
                const rowColor = row.status === 'done' ? '#d1fae5' : row.status === 'failed' ? '#fee2e2' : 'transparent';
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 160px 1fr 32px', gap: 8, marginBottom: 8, padding: '6px 8px', borderRadius: 8, background: rowColor, transition: 'background 0.4s' }}>
                    <select className="form-input" style={{ margin: 0 }} value={row.clientId} disabled={agentRunning}
                      onChange={e => {
                        const newClientId = e.target.value;
                        const client = clients.find(c => c.clientId === newClientId);
                        const assignedUsername = client?.assignedCaregivers?.[0] || '';
                        const matchedCg = caregivers.find(cg => cg.username === assignedUsername);
                        setAgentRows(rows => rows.map((r, ri) => ri === i
                          ? { ...r, clientId: newClientId, assignedTo: matchedCg ? matchedCg.username : r.assignedTo }
                          : r));
                      }}>
                      <option value="">— Select client —</option>
                      {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                    </select>

                    <input className="form-input" style={{ margin: 0 }} type="date" value={row.weekBegin} disabled={agentRunning}
                      onChange={e => setAgentRows(rows => rows.map((r, ri) => ri === i ? { ...r, weekBegin: e.target.value } : r))} />

                    <select className="form-input" style={{ margin: 0 }} value={row.assignedTo} disabled={agentRunning}
                      onChange={e => setAgentRows(rows => rows.map((r, ri) => ri === i ? { ...r, assignedTo: e.target.value } : r))}>
                      <option value="">— Select caregiver —</option>
                      {caregivers.filter(c => c.role !== 'admin').map(cg => (
                        <option key={cg.username} value={cg.username}>{cg.firstName} {cg.lastName}</option>
                      ))}
                    </select>

                    {/* Status icon or remove button */}
                    {agentRunning ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }} title={row.error || row.status}>
                        {statusIcon}
                      </div>
                    ) : (
                      <button type="button" title="Remove row"
                        style={{ border: 'none', background: 'none', cursor: agentRows.length === 1 ? 'not-allowed' : 'pointer', color: agentRows.length === 1 ? '#ccc' : '#888', fontSize: 18, lineHeight: 1, padding: 0, alignSelf: 'center', justifySelf: 'center' }}
                        disabled={agentRows.length === 1}
                        onClick={() => setAgentRows(rows => rows.filter((_, ri) => ri !== i))}>
                        ×
                      </button>
                    )}

                    {/* Inline error */}
                    {row.error && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#dc2626', marginTop: -4 }}>⚠ {row.error}</div>}
                  </div>
                );
              })}

              {/* Progress bar (visible while running) */}
              {batchProgress && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ height: 6, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{
                      height: '100%', borderRadius: 3, transition: 'width 0.5s ease',
                      background: 'linear-gradient(90deg, #1a2b4a, #2d5a8e)',
                      width: `${batchProgress.total ? ((batchProgress.succeeded + batchProgress.failed) / batchProgress.total) * 100 : 0}%`,
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {batchProgress.status === 'complete'
                      ? `Done — ${batchProgress.succeeded} succeeded, ${batchProgress.failed} failed`
                      : `Processing… ${batchProgress.succeeded + batchProgress.failed} of ${batchProgress.total}`}
                  </span>
                </div>
              )}

              {/* Action row */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                <button type="button" className="btn"
                  style={{ fontSize: 13, padding: '6px 14px', border: '1px dashed #94a3b8', background: 'transparent', color: '#475569', borderRadius: 6, cursor: agentRows.length >= 3 || agentRunning ? 'not-allowed' : 'pointer', opacity: agentRows.length >= 3 || agentRunning ? 0.5 : 1 }}
                  disabled={agentRows.length >= 3 || agentRunning}
                  onClick={() => setAgentRows(rows => [...rows, EMPTY_ROW()])}>
                  + Add Client
                </button>

                <button className="btn btn-teal" type="submit" disabled={agentRunning}
                  style={{ background: agentRunning ? undefined : 'linear-gradient(135deg, #1a2b4a, #2d5a8e)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                  {agentRunning
                    ? `⏳ Generating ${agentRows.length} note${agentRows.length !== 1 ? 's' : ''}…`
                    : `✦ Generate ${agentRows.length > 1 ? `${agentRows.length} Notes` : 'Note'}`}
                </button>
              </div>
            </form>

            {clients.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12 }}>Add clients below, then upload their Functional Assessment to use the AI agent.</p>
            )}
          </div>
        </div>}

        {/* ── AUTO-SCHEDULE ── */}
        {section === 'ai-agent' && <div className="card" style={{ marginTop: 16 }}>
          {/* Collapsible header */}
          <div
            className="card-header"
            onClick={() => setScheduleOpen(o => !o)}
            style={{
              background: 'linear-gradient(135deg, #1a3a4a 0%, #2d6a8e 100%)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              userSelect: 'none',
            }}
          >
            <span>↻ Auto-Schedule — Weekly Note Generation</span>
            <span style={{ fontSize: 12, opacity: 0.8, fontWeight: 400 }}>
              {scheduleOpen ? '▲ collapse' : '▼ expand'}
            </span>
          </div>

          {scheduleOpen && <div className="card-body">
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
              Enrolled clients have their weekly progress notes generated automatically every{' '}
              <strong>Friday evening</strong> (covering the Sat–Fri week that just ended).
              Each note is placed in <strong>Pending Signature</strong> status and the caregiver
              receives an SMS notification to sign — using their existing SMS settings.
            </p>

            {/* Enrolled list */}
            {schedule.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 32px', gap: 8, marginBottom: 6, paddingLeft: 2 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Caregiver</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Auto-Gen</span>
                  <span />
                </div>
                {schedule.map(entry => (
                  <div key={entry.clientId} style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 100px 32px', gap: 8,
                    alignItems: 'center', padding: '8px 10px', borderRadius: 8,
                    background: entry.enabled ? 'var(--cream)' : '#f8f9fa',
                    marginBottom: 6, border: '1px solid var(--border)',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: entry.enabled ? 'var(--navy)' : 'var(--muted)' }}>
                      {entry.clientName || entry.clientId}
                    </span>
                    <span style={{ fontSize: 13, color: entry.enabled ? 'var(--navy)' : 'var(--muted)' }}>
                      {entry.caregiverName || entry.caregiverId}
                    </span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        onChange={() => handleToggleScheduleEnabled(entry)}
                        style={{ width: 15, height: 15, cursor: 'pointer' }}
                      />
                      <span style={{ color: entry.enabled ? '#16a34a' : 'var(--muted)', fontWeight: 600 }}>
                        {entry.enabled ? 'On' : 'Off'}
                      </span>
                    </label>
                    <button
                      type="button"
                      title="Remove from schedule"
                      onClick={() => handleRemoveScheduleRow(entry.clientId, entry.clientName)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#888', fontSize: 18, lineHeight: 1, padding: 0 }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new enrollment row */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Client</div>
                <select
                  className="form-input"
                  style={{ margin: 0 }}
                  value={newScheduleRow.clientId}
                  onChange={e => {
                    const clientId = e.target.value;
                    const client = clients.find(c => c.clientId === clientId);
                    const assigned = client?.assignedCaregivers?.[0] || '';
                    const matched  = caregivers.find(cg => cg.username === assigned);
                    setNewScheduleRow(r => ({ ...r, clientId, caregiverId: matched ? matched.username : r.caregiverId }));
                  }}
                >
                  <option value="">— Select client —</option>
                  {clients
                    .filter(c => !schedule.some(s => s.clientId === c.clientId))
                    .map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                </select>
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Caregiver</div>
                <select
                  className="form-input"
                  style={{ margin: 0 }}
                  value={newScheduleRow.caregiverId}
                  onChange={e => setNewScheduleRow(r => ({ ...r, caregiverId: e.target.value }))}
                >
                  <option value="">— Select caregiver —</option>
                  {caregivers.filter(c => c.role !== 'admin').map(cg => (
                    <option key={cg.username} value={cg.username}>{cg.firstName} {cg.lastName}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="btn"
                onClick={handleAddScheduleRow}
                disabled={!newScheduleRow.clientId || !newScheduleRow.caregiverId}
                style={{
                  fontSize: 13, padding: '10px 18px',
                  border: '1px dashed #94a3b8', background: 'transparent',
                  color: '#475569', borderRadius: 6,
                  cursor: !newScheduleRow.clientId || !newScheduleRow.caregiverId ? 'not-allowed' : 'pointer',
                  opacity: !newScheduleRow.clientId || !newScheduleRow.caregiverId ? 0.5 : 1,
                  alignSelf: 'flex-end',
                }}
              >
                + Enroll
              </button>
            </div>

            {/* Generate Now */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div style={{ flex: 1 }}>
                {scheduleResult && (
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Last run: {scheduleResult.generated} generated, {scheduleResult.skipped} skipped
                    {scheduleResult.smsSent > 0 ? `, ${scheduleResult.smsSent} SMS sent` : ''}
                    {scheduleResult.errors > 0 ? `, ${scheduleResult.errors} error(s)` : ''}
                  </span>
                )}
                {!scheduleResult && schedule.length === 0 && (
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>No clients enrolled yet. Add a client above to get started.</span>
                )}
                {!scheduleResult && schedule.length > 0 && (
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                    {schedule.filter(s => s.enabled).length} client{schedule.filter(s => s.enabled).length !== 1 ? 's' : ''} enrolled · auto-runs every Friday evening
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-teal"
                onClick={handleRunScheduleNow}
                disabled={scheduleRunning || schedule.filter(s => s.enabled).length === 0}
                style={{
                  background: scheduleRunning ? undefined : 'linear-gradient(135deg, #1a3a4a, #2d6a8e)',
                  whiteSpace: 'nowrap',
                  opacity: schedule.filter(s => s.enabled).length === 0 ? 0.5 : 1,
                }}
              >
                {scheduleRunning ? '⏳ Generating…' : '↻ Generate Now for Current Week'}
              </button>
            </div>
          </div>}
        </div>}

        {/* ── AI AGENT EVV ── */}
        {section === 'ai-agent-evv' && <div className="card">
          <div className="card-header" style={{ background: 'linear-gradient(135deg, #1a4a2b 0%, #2d8e5a 100%)', color: '#fff' }}>
            📡 AI Agent EVV — Generate Notes from Time &amp; Attendance Report
          </div>
          <div className="card-body">
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
              Upload a Time &amp; Attendance report (CSV or Excel). The agent reads the <strong>Visit In</strong> and <strong>Visit Out</strong> columns
              and uses those actual times when generating the weekly progress note — no time calculations needed.
              A Functional Assessment must still be on file for ADL/IADL checkboxes and narratives.
            </p>

            <form onSubmit={handleEvvGenerate}>
              {/* Step 1 — Select client / week / caregiver */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                  Step 1 — Select Client, Week &amp; Caregiver
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group-card" style={{ flex: '1 1 180px' }}>
                    <label className="form-label">Client</label>
                    <select className="form-input" value={evvForm.clientId}
                      onChange={e => handleEvvClientChange(e.target.value)}>
                      <option value="">— Select client —</option>
                      {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                    </select>
                  </div>
                  <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                    <label className="form-label">Week Beginning (Saturday)</label>
                    <input className="form-input" type="date" value={evvForm.weekBegin}
                      onChange={e => handleEvvWeekChange(e.target.value)} />
                  </div>
                  {evvCaregiverMappings.length === 0 && (() => {
                    const assignedCgsList = evvForm.clientId
                      ? (clients.find(c => c.clientId === evvForm.clientId)?.assignedCaregivers || [])
                          .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'))
                      : [];
                    return assignedCgsList.length > 1 ? (
                      <div className="form-group-card" style={{ flex: '1 1 200px' }}>
                        <label className="form-label">Assign to Caregivers</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                          {assignedCgsList.map(u => {
                            const cg = caregivers.find(c => c.username === u);
                            if (!cg) return null;
                            const isChecked = evvSelectedCaregiverUsernames.includes(u);
                            return (
                              <label key={u} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: isChecked ? 600 : 400, color: isChecked ? 'var(--navy)' : 'var(--muted)' }}>
                                <input type="checkbox" checked={isChecked}
                                  onChange={e => setEvvSelectedCaregiverUsernames(prev =>
                                    e.target.checked ? [...prev, u] : prev.filter(x => x !== u)
                                  )} />
                                {cg.firstName} {cg.lastName}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="form-group-card" style={{ flex: '1 1 200px' }}>
                        <label className="form-label">Assign to Caregiver</label>
                        <select className="form-input" value={evvForm.assignedTo}
                          onChange={e => setEvvForm(f => ({ ...f, assignedTo: e.target.value }))}>
                          <option value="">— Select caregiver —</option>
                          {caregivers.filter(c => c.role !== 'admin').map(cg => (
                            <option key={cg.username} value={cg.username}>{cg.firstName} {cg.lastName}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                  {evvCaregiverMappings.length > 0 && (
                    <div style={{ flex: '1 1 200px', padding: '10px 0', fontSize: 13, color: 'var(--muted)', alignSelf: 'flex-end', paddingBottom: 8 }}>
                      {evvCaregiverMappings.length} caregivers detected in T&amp;A report — map below
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2 — Select T&A report */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                  Step 2 — Select Time &amp; Attendance Report
                </div>

                {evvForm.clientId ? (
                  evvLoadingReports ? (
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading saved reports…</div>
                  ) : evvClientReports.length > 0 ? (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div className="form-group-card" style={{ flex: '1 1 280px' }}>
                        <label className="form-label">Saved T&amp;A Report</label>
                        <select className="form-input" value={evvSelectedReportId}
                          onChange={e => handleEvvStoredReportSelect(e.target.value)}
                          disabled={evvParsing}>
                          <option value="">— Select a saved report —</option>
                          {evvClientReports.map(r => (
                            <option key={r.reportId} value={r.reportId}>
                              {r.fileName} · {new Date(r.uploadedAt).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', paddingBottom: 8 }}>or</div>
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 2,
                        cursor: evvParsing ? 'not-allowed' : 'pointer',
                        padding: '10px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                        background: 'var(--cream)', color: 'var(--navy)',
                        border: '1.5px solid var(--border)', opacity: evvParsing ? 0.6 : 1,
                      }}>
                        <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          style={{ display: 'none' }} disabled={evvParsing} onChange={handleEvvFileUpload} />
                        📂 Upload New Report
                      </label>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        cursor: evvParsing ? 'not-allowed' : 'pointer',
                        padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                        background: evvParsing ? '#e8edf2' : 'linear-gradient(135deg, #1a4a2b, #2d8e5a)',
                        color: evvParsing ? 'var(--muted)' : '#fff', opacity: evvParsing ? 0.7 : 1,
                      }}>
                        <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          style={{ display: 'none' }} disabled={evvParsing} onChange={handleEvvFileUpload} />
                        {evvParsing
                          ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /> Parsing…</>
                          : <>📂 Upload T&amp;A Report</>}
                      </label>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>No saved reports for this client yet. CSV or Excel (.xlsx) · max 10 MB</span>
                    </div>
                  )
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>Select a client above to see their saved T&amp;A reports.</div>
                )}

                {evvParsing && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>
                    <span style={{ display:'inline-block', width:12, height:12, border:'2px solid var(--teal)', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', marginRight:6 }} />
                    Parsing report…
                  </div>
                )}
                {evvParseError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)', background: '#fdf2f2', padding: '8px 12px', borderRadius: 6 }}>
                    {evvParseError}
                  </div>
                )}
              </div>

              {/* Step 2.5 — Caregiver Mapping (multi-caregiver T&A reports only) */}
              {evvCaregiverMappings.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                    Step 2.5 — Map Caregivers
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                    The report contains {evvCaregiverMappings.length} caregivers with timed visits. Match each name to a registered user — one note will be generated per caregiver.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {evvCaregiverMappings.map((m, i) => {
                      const visitCount = DAYS.filter(d => m.times.timeIn[d]).length;
                      const invite = evvInviteCodes[m.taName];
                      return (
                        <div key={m.taName} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 180px' }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{m.taName}</div>
                              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{visitCount} visit day{visitCount !== 1 ? 's' : ''} this week</div>
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--muted)' }}>→</div>
                            <div style={{ flex: '1 1 220px' }}>
                              <select className="form-input" style={{ margin: 0 }}
                                value={m.assignedTo}
                                onChange={e => setEvvCaregiverMappings(prev => prev.map((r, ri) => ri === i ? { ...r, assignedTo: e.target.value } : r))}>
                                <option value="">— Select registered caregiver —</option>
                                {caregivers.filter(c => c.role !== 'admin').map(cg => (
                                  <option key={cg.username} value={cg.username}>{cg.firstName} {cg.lastName}</option>
                                ))}
                                <option value="__new__">→ Register new caregiver…</option>
                              </select>
                            </div>
                          </div>
                          {m.assignedTo === '__new__' && (
                            <div style={{ marginLeft: 14, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13 }}>
                              {!invite || invite.status === 'idle' ? (
                                <>
                                  <strong>{m.taName}</strong> isn't registered yet.{' '}
                                  <button type="button" className="btn" style={{ fontSize: 12, padding: '4px 12px', marginLeft: 10 }}
                                    onClick={() => handleEvvInvite(m.taName)}>
                                    Generate invite code
                                  </button>
                                </>
                              ) : invite.status === 'generating' ? (
                                <span style={{ color: 'var(--muted)' }}>Generating invite code…</span>
                              ) : invite.status === 'done' ? (
                                <>
                                  <span style={{ color: 'var(--muted)' }}>Invite code for <strong>{m.taName}</strong>: </span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.1em', background: '#fff', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', marginRight: 8 }}>{invite.code}</span>
                                  <button type="button" className="btn" style={{ fontSize: 12, padding: '4px 10px' }}
                                    onClick={() => { navigator.clipboard.writeText(invite.code); showToast('Code copied!', 'success'); }}>
                                    Copy
                                  </button>
                                  <span style={{ display: 'block', marginTop: 4, color: 'var(--muted)', fontSize: 12 }}>
                                    Share this code. Once they register, refresh and select them here.
                                  </span>
                                </>
                              ) : (
                                <span style={{ color: 'red' }}>Failed to generate code — try again from Invite Codes.</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 3 — Visit time preview (multi-caregiver summary) */}
              {evvCaregiverMappings.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                    Step 3 — Visit Times by Caregiver
                  </div>
                  {evvCaregiverMappings.map(m => {
                    const visitDays = DAYS.filter(d => m.times.timeIn[d]);
                    return (
                      <div key={m.taName} style={{ marginBottom: 8, padding: '10px 14px', background: 'var(--cream)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{m.taName} · {visitDays.length} day{visitDays.length !== 1 ? 's' : ''}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 12 }}>
                          {visitDays.length ? visitDays.map(d => (
                            <span key={d} style={{ background: '#fff', padding: '3px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
                              {d}: {m.times.timeIn[d]}–{m.times.timeOut[d]}
                            </span>
                          )) : <span style={{ color: 'var(--muted)' }}>No timed visits this week</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Step 3 — Visit time preview (single caregiver) */}
              {evvMappedTimes && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                    Step 3 — Visit Times from {evvFileName}
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
                      <thead>
                        <tr style={{ background: 'var(--cream)' }}>
                          {DAYS.map(d => (
                            <th key={d} style={{ padding: '8px 14px', border: '1px solid var(--border)', fontWeight: 600, textAlign: 'center', minWidth: 80 }}>{d}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {DAYS.map(d => (
                            <td key={d} style={{ padding: '8px 14px', border: '1px solid var(--border)', textAlign: 'center', color: evvMappedTimes.timeIn[d] ? 'var(--navy)' : 'var(--muted)', fontWeight: evvMappedTimes.timeIn[d] ? 600 : 400 }}>
                              {evvMappedTimes.timeIn[d] || '—'}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          {DAYS.map(d => (
                            <td key={d} style={{ padding: '8px 14px', border: '1px solid var(--border)', textAlign: 'center', color: evvMappedTimes.timeOut[d] ? '#2a7f7f' : 'var(--muted)', fontWeight: evvMappedTimes.timeOut[d] ? 600 : 400 }}>
                              {evvMappedTimes.timeOut[d] || '—'}
                            </td>
                          ))}
                        </tr>
                        <tr style={{ background: 'var(--cream)' }}>
                          {DAYS.map(d => (
                            <td key={d} style={{ padding: '4px 14px', border: '1px solid var(--border)', textAlign: 'center', fontSize: 10, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {evvMappedTimes.timeIn[d] ? 'In / Out' : '—'}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                    {DAYS.filter(d => evvMappedTimes.timeIn[d]).length} visit day{DAYS.filter(d => evvMappedTimes.timeIn[d]).length !== 1 ? 's' : ''} mapped to this week.
                    Dates outside the selected week are excluded.
                  </p>
                  {/* Per-caregiver breakdown when T&A report has caregiver name data */}
                  {evvCaregiverMappings.length === 0 && (() => {
                    const assignedList = evvForm.clientId
                      ? (clients.find(c => c.clientId === evvForm.clientId)?.assignedCaregivers || [])
                          .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'))
                      : [];
                    if (assignedList.length <= 1 || !evvVisits?.some(v => v.caregiverName)) return null;
                    const breakdown = assignedList.map(u => {
                      const cg = caregivers.find(c => c.username === u);
                      const fn = cg ? `${cg.firstName} ${cg.lastName}` : '';
                      const cgVisits = evvVisits.filter(v => {
                        if (!v.caregiverName) return false;
                        const vn = v.caregiverName.toLowerCase();
                        return vn === fn.toLowerCase() ||
                               vn.includes((cg?.firstName || '').toLowerCase()) ||
                               fn.toLowerCase().includes(vn.split(' ')[0]);
                      });
                      const days = cgVisits.length > 0
                        ? DAYS.filter(d => mapVisitsToWeekDays(cgVisits, evvForm.weekBegin).timeIn[d]).length
                        : null;
                      return { fn, days };
                    }).filter(x => x.days !== null);
                    if (!breakdown.length) return null;
                    return (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: '#f0faf5', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 12 }}>
                        <strong style={{ color: 'var(--navy)' }}>Per-caregiver:</strong>
                        {breakdown.map(({ fn, days }) => (
                          <span key={fn} style={{ marginLeft: 12, color: 'var(--navy)' }}>
                            {fn} — <strong>{days}</strong> day{days !== 1 ? 's' : ''}
                          </span>
                        ))}
                        <span style={{ marginLeft: 10, color: 'var(--muted)' }}>· Each caregiver will receive their own visit times.</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Step 4 — Generate */}
              <div>
                {(evvMappedTimes || evvCaregiverMappings.length > 0) && (
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 10 }}>
                    Step 4 — Generate Note{evvCaregiverMappings.length > 1 ? 's' : ''}
                  </div>
                )}
                {(() => {
                  const assignedCgsBtn = evvForm.clientId
                    ? (clients.find(c => c.clientId === evvForm.clientId)?.assignedCaregivers || [])
                        .filter(u => caregivers.some(cg => cg.username === u && cg.role !== 'admin'))
                    : [];
                  const isCheckboxMultiBtn = evvCaregiverMappings.length === 0 && assignedCgsBtn.length > 1;
                  const checkedCount = isCheckboxMultiBtn ? evvSelectedCaregiverUsernames.length : 0;
                  const mappedCount = evvCaregiverMappings.filter(m => m.assignedTo && m.assignedTo !== '__new__').length;
                  const isDisabled = evvRunning || !evvForm.clientId || !evvForm.weekBegin || (
                    evvCaregiverMappings.length > 0
                      ? evvCaregiverMappings.some(m => !m.assignedTo) || mappedCount === 0
                      : isCheckboxMultiBtn
                        ? (!checkedCount || !evvMappedTimes)
                        : (!evvForm.assignedTo || !evvMappedTimes)
                  );
                  const hasReport = evvMappedTimes || evvCaregiverMappings.length > 0;
                  const btnLabel = evvRunning ? '⏳ Generating…'
                    : evvCaregiverMappings.length > 0
                      ? `📡 Generate ${mappedCount} EVV Note${mappedCount !== 1 ? 's' : ''}`
                      : isCheckboxMultiBtn && checkedCount > 0
                        ? `📡 Generate ${checkedCount} EVV Note${checkedCount !== 1 ? 's' : ''}`
                        : '📡 Generate EVV Note';
                  return (
                    <>
                      <button className="btn" type="submit" disabled={isDisabled}
                        style={{ background: (isDisabled || !hasReport) ? undefined : 'linear-gradient(135deg, #1a4a2b, #2d8e5a)', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}>
                        {btnLabel}
                      </button>
                      {!hasReport && evvCaregiverMappings.length === 0 && (
                        <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--muted)' }}>Upload a T&A report above to enable generation.</span>
                      )}
                    </>
                  );
                })()}

                {/* Multi-caregiver generation results */}
                {evvMultiResults && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--muted)', marginBottom: 8 }}>
                      Generation Results
                    </div>
                    {evvMultiResults.map((r, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 6, borderRadius: 6,
                        background: r.status === 'done' ? '#f0faf5' : r.status === 'error' ? '#fdf2f2' : 'var(--cream)',
                        border: `1px solid ${r.status === 'done' ? '#a7f3d0' : r.status === 'error' ? '#f5c6c6' : 'var(--border)'}`,
                      }}>
                        <span>{r.status === 'done' ? '✅' : r.status === 'error' ? '❌' : '⏳'}</span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{r.taName}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {r.status === 'done' ? 'Note generated — awaiting signature'
                            : r.status === 'error' ? r.error
                            : 'Generating…'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>}

        {/* ── CREATE NOTE ── */}
        {section === 'create-note' && <div className="card">
          <div className="card-header">📋 Create Progress Note for Caregiver</div>
          <div className="card-body">
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 16 }}>
              Select a caregiver to create a note on their behalf. You can fill it out fully or leave it for them to complete.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group-card" style={{ flex: 1, minWidth: 200 }}>
                <label className="form-label">Select Caregiver</label>
                <select className="form-input" value={selectedCaregiver}
                  onChange={e => setSelectedCaregiver(e.target.value)}>
                  <option value="">— Choose a caregiver —</option>
                  {caregivers.filter(c => c.role !== 'admin').map(cg => (
                    <option key={cg.username} value={cg.username}>
                      {cg.firstName} {cg.lastName} ({cg.email})
                    </option>
                  ))}
                </select>
              </div>
              <button className="btn btn-teal" onClick={handleCreateNote}
                disabled={!selectedCaregiver || creatingNote}>
                {creatingNote ? 'Creating…' : '+ Create Note'}
              </button>
            </div>
            {caregivers.filter(c => c.role !== 'admin').length === 0 && (
              <p style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
                No caregivers registered yet. Add them from the Users section.
              </p>
            )}
          </div>
        </div>}

        {/* ── CLIENTS ── */}
        {section === 'clients' && <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>👤 Client Management</span>
            <button className="btn btn-teal btn-sm" onClick={() => { resetClientForm(); setShowClientForm(v => !v); }}>
              {showClientForm && !editingClientId ? '✕ Cancel' : '+ Add Client'}
            </button>
          </div>

          {/* Add / Edit Client Form */}
          {showClientForm && (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--cream)' }}>
              <form onSubmit={handleSaveClient}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
                  <div className="form-group-card" style={{ flex: '1 1 180px' }}>
                    <label className="form-label">Client Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input className="form-input" type="text" value={clientForm.clientName}
                      onChange={e => setClientForm(f => ({ ...f, clientName: e.target.value }))}
                      placeholder="Full name" />
                  </div>
                  <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                    <label className="form-label">Date of Birth</label>
                    <input className="form-input" type="date" value={clientForm.dob}
                      onChange={e => setClientForm(f => ({ ...f, dob: e.target.value }))} />
                  </div>
                  <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                    <label className="form-label">Medicaid ID</label>
                    <input className="form-input" type="text" value={clientForm.medicaidId}
                      onChange={e => setClientForm(f => ({ ...f, medicaidId: e.target.value }))}
                      placeholder="ID number" />
                  </div>
                </div>
                <div className="form-group-card" style={{ marginBottom: 14 }}>
                  <label className="form-label">Assign to Caregiver(s)</label>
                  {caregivers.filter(c => c.role !== 'admin').length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--muted)', margin: '4px 0 0' }}>No caregivers registered yet.</p>
                  ) : (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                      {caregivers.filter(c => c.role !== 'admin').map(cg => (
                        <label key={cg.username} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                          <input type="checkbox"
                            checked={clientForm.assignedCaregivers.includes(cg.username)}
                            onChange={() => toggleCaregiverAssignment(cg.username)} />
                          {cg.firstName} {cg.lastName}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {editingClientId && (
                  <div className="form-group-card" style={{ marginBottom: 14 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                      <input type="checkbox"
                        checked={clientForm.serviceStatus === 'service-ended'}
                        onChange={e => setClientForm(f => ({ ...f, serviceStatus: e.target.checked ? 'service-ended' : 'active' }))} />
                      No longer needs services (stops Prior Authorization reminders for this client)
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-teal" type="submit" disabled={savingClient}>
                    {savingClient ? 'Saving…' : editingClientId ? 'Update Client' : 'Save Client'}
                  </button>
                  <button className="btn btn-ghost" type="button" onClick={resetClientForm}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          <div className="card-body" style={{ padding: 0 }}>
            {clients.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">👤</div>
                <h3>No clients yet</h3>
                <p>Add a client above to assign them to caregivers and set their Medicaid ID.</p>
              </div>
            ) : (
              <>
                <div className="admin-table-wrap">
                  <table className="notes-table">
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th>Date of Birth</th>
                        <th>Medicaid ID</th>
                        <th>Assigned Caregivers</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map(client => (
                        <tr key={client.clientId}>
                          <td style={{ fontWeight: 500 }}>
                            {client.clientName}
                            {client.serviceStatus === 'service-ended' && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--muted)', background: '#eee', borderRadius: 10, padding: '2px 8px' }}>
                                Service ended
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--muted)' }}>{client.dob ? formatDate(client.dob) : '—'}</td>
                          <td style={{ fontSize: 13 }}>{client.medicaidId || <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Not set</span>}</td>
                          <td style={{ fontSize: 13 }}>
                            {(client.assignedCaregivers || []).length === 0
                              ? <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>None</span>
                              : (client.assignedCaregivers || []).map(u => {
                                  const cg = caregivers.find(c => c.username === u);
                                  return cg ? `${cg.firstName} ${cg.lastName}` : u;
                                }).join(', ')
                            }
                          </td>
                          <td style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => handleEditClient(client)}>Edit</button>
                            <button className="btn btn-ghost btn-sm"
                              style={{ color: '#2d5a8e', borderColor: '#2d5a8e' }}
                              onClick={() => openAssessmentModal(client.clientId)}>Assessment</button>
                            <button className="btn btn-ghost btn-sm"
                              style={{ color: '#1a6b4a', borderColor: '#1a6b4a' }}
                              onClick={() => openTaModal(client.clientId, client.clientName)}>T&amp;A Reports</button>
                            <button className="btn btn-ghost btn-sm"
                              style={{ color: '#8e5a2d', borderColor: '#8e5a2d' }}
                              onClick={() => openPriorAuthModal(client.clientId, client.clientName)}>Prior Auths</button>
                            <button className="btn btn-sm"
                              style={{ background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                              onClick={() => handleDeleteClient(client.clientId, client.clientName)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>}

        {/* ── PRIOR AUTHORIZATIONS ── */}
        {section === 'prior-auths' && <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⚠️ Expiring Authorizations</span>
            <button className="btn btn-teal btn-sm" onClick={loadExpiringAuths} disabled={loadingExpiringAuths}>
              {loadingExpiringAuths ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {expiringAuthsError && (
              <div style={{ margin: 16, fontSize: 13, color: 'var(--danger)', background: '#fdf2f2', padding: '8px 12px', borderRadius: 6 }}>{expiringAuthsError}</div>
            )}
            {loadingExpiringAuths ? (
              <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : expiringAuths.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <h3>Nothing expiring soon</h3>
                <p>No active Prior Authorizations are within 30 days of their end date.</p>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="notes-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Days Until Expiry</th>
                      <th>Auth #</th>
                      <th>Payer</th>
                      <th>End Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiringAuths.map(a => {
                      const overdue = a.daysUntilExpiry < 0;
                      const urgent = !overdue && a.daysUntilExpiry <= 7;
                      const color = overdue ? 'var(--danger)' : urgent ? '#c98a3a' : '#a08a1a';
                      return (
                        <tr key={`${a.clientId}-${a.authId}`}>
                          <td style={{ fontWeight: 500 }}>{a.clientName || a.clientId}</td>
                          <td>
                            <span style={{ fontWeight: 700, color }}>
                              {overdue ? `Expired ${Math.abs(a.daysUntilExpiry)}d ago` : `${a.daysUntilExpiry}d`}
                            </span>
                            {a.snoozedUntil && (
                              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Snoozed until {formatDate(a.snoozedUntil)}</div>
                            )}
                          </td>
                          <td style={{ fontSize: 13 }}>{a.authNumber || '—'}</td>
                          <td style={{ fontSize: 13 }}>{a.payer || '—'}</td>
                          <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{formatDate(a.endDate)}</td>
                          <td style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => openPriorAuthModal(a.clientId, a.clientName)}>Review</button>
                            <select defaultValue="" onChange={e => { if (e.target.value) { handleSnoozePriorAuth(a.clientId, a.authId, Number(e.target.value)); e.target.value = ''; } }}
                              style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)' }}>
                              <option value="" disabled>Snooze…</option>
                              <option value="7">7 days</option>
                              <option value="14">14 days</option>
                              <option value="30">30 days</option>
                              <option value="60">60 days</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>}

        {/* ── ALL NOTES ── */}
        {section === 'notes' && <div className="card" style={{ overflow: 'hidden' }}>
          {/* Card header */}
          <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg,#1a2b4a,#243d6e)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Progress Notes</span>
              <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                {filteredNotes.length}{allNotes.length !== filteredNotes.length ? ` of ${allNotes.length}` : ''} notes
              </span>
              {hasColFilters && (
                <button onClick={clearColFilters} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '3px 10px', fontSize: 12 }}>
                  ✕ Clear filters
                </button>
              )}
            </div>
            <input
              type="text" placeholder="Search client or caregiver…" value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '7px 14px', color: '#fff', fontSize: 13, width: 230, outline: 'none' }}
            />
          </div>

          {/* Column filter row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.6fr 1fr 1fr auto', gap: 0, borderBottom: '2px solid var(--border)', background: '#f8f9fc' }}>
            {[
              {
                label: 'CLIENT NAME', sortKey: 'clientName',
                filter: <select className="form-input" style={{ margin: '6px 8px', fontSize: 12, padding: '5px 8px', width: 'calc(100% - 16px)' }}
                  value={colFilters.clientName} onChange={e => setColFilters(f => ({ ...f, clientName: e.target.value }))}>
                  <option value="">All</option>
                  {[...new Set(clients.map(c => c.clientName).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>,
              },
              {
                label: 'CAREGIVER', sortKey: 'caregiver',
                filter: <select className="form-input" style={{ margin: '6px 8px', fontSize: 12, padding: '5px 8px', width: 'calc(100% - 16px)' }}
                  value={colFilters.caregiver} onChange={e => setColFilters(f => ({ ...f, caregiver: e.target.value }))}>
                  <option value="">All</option>
                  {caregivers.filter(c => c.role !== 'admin').map(cg => (
                    <option key={cg.username} value={cg.username}>{cg.firstName} {cg.lastName}</option>
                  ))}
                </select>,
              },
              {
                label: 'WEEK BEGINNING', sortKey: 'weekBegin',
                filter: <div style={{ display: 'flex', gap: 4, margin: '6px 8px' }}>
                  <input type="date" className="form-input" style={{ fontSize: 11, padding: '4px 6px', flex: 1 }}
                    value={colFilters.weekFrom} onChange={e => setColFilters(f => ({ ...f, weekFrom: e.target.value }))} />
                  <input type="date" className="form-input" style={{ fontSize: 11, padding: '4px 6px', flex: 1 }}
                    value={colFilters.weekTo} onChange={e => setColFilters(f => ({ ...f, weekTo: e.target.value }))} />
                </div>,
              },
              {
                label: 'STATUS', sortKey: null,
                filter: <select className="form-input" style={{ margin: '6px 8px', fontSize: 12, padding: '5px 8px', width: 'calc(100% - 16px)' }}
                  value={colFilters.status} onChange={e => setColFilters(f => ({ ...f, status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="draft">Draft</option>
                  <option value="pending_signature">Pending Sig.</option>
                  <option value="signed">Signed</option>
                </select>,
              },
              {
                label: 'SAVED', sortKey: 'savedAt',
                filter: <div style={{ height: 34, margin: '6px 8px' }} />,
              },
              { label: 'ACTIONS', sortKey: null, filter: null },
            ].map(({ label, sortKey, filter }) => (
              <div key={label} style={{ borderRight: '1px solid var(--border)' }}>
                <div
                  onClick={sortKey ? () => handleSort(sortKey) : undefined}
                  style={{ padding: '10px 8px 4px', fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none', display: 'flex', alignItems: 'center' }}>
                  {label}
                  {sortKey && <SortArrow col={sortKey} />}
                </div>
                {filter}
              </div>
            ))}
          </div>

          {/* Table body */}
          <div style={{ padding: 0 }}>
            {filteredNotes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h3>{allNotes.length === 0 ? 'No notes yet' : 'No notes match your filters'}</h3>
                <p>{allNotes.length === 0 ? 'Generate a note using the AI Agent above.' : 'Try adjusting the filters.'}</p>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '25%' }} /><col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} /><col style={{ width: '14%' }} />
                      <col style={{ width: '12%' }} /><col style={{ width: '13%' }} />
                    </colgroup>
                    <tbody>
                      {filteredNotes.map((note, idx) => (
                        <tr key={note.id}
                          style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc', borderBottom: '1px solid #f0f0f4' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                          onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc'}>
                          <td style={{ padding: '12px 8px 12px 16px', fontWeight: 600, fontSize: 13 }}>
                            <Link to={`/notes/${note.id}`} style={{ color: 'var(--navy)', textDecoration: 'none' }}>
                              {note.clientName || <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontWeight: 400 }}>No client</span>}
                            </Link>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: 13, color: '#374151' }}>
                            {note.assignedToName || note.caregiverName || '—'}
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: 13, color: 'var(--muted)' }}>
                            {formatDate(note.weekBegin)}
                          </td>
                          <td style={{ padding: '12px 8px' }}>{statusBadge(note)}</td>
                          <td style={{ padding: '12px 8px', fontSize: 12, color: 'var(--muted)' }}>
                            {note.savedAt ? new Date(note.savedAt).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <Link to={`/notes/${note.id}`} className="btn btn-ghost btn-sm" style={{ padding: '4px 10px' }}>Edit</Link>
                              <button className="btn btn-sm"
                                style={{ padding: '4px 10px', background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                                onClick={() => handleDeleteNote(note.id, note.clientName)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="admin-mobile-list">
                  {filteredNotes.map(note => (
                    <div key={note.id} className="mobile-note-card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div className="mobile-note-title">{note.clientName || 'No client assigned'}</div>
                        <div style={{ flexShrink: 0 }}>
                          {statusBadge(note)}
                        </div>
                      </div>
                      <div className="mobile-note-meta">
                        {note.assignedToName || note.caregiverName || '—'} · Week of {formatDate(note.weekBegin)}
                      </div>
                      <div className="mobile-note-actions">
                        <Link to={`/notes/${note.id}`} className="btn btn-teal btn-sm" style={{ flex: 1, justifyContent: 'center' }}>Edit</Link>
                        <button className="btn btn-sm" style={{ flex: 1, background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                          onClick={() => handleDeleteNote(note.id, note.clientName)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>}

        {/* ── USERS ── */}
        {section === 'users' && (
          <>
            <div className="users-view-tabs" role="tablist" aria-label="Users section view">
              <button type="button" role="tab" aria-selected={usersView === 'accounts'}
                className={`users-view-tab${usersView === 'accounts' ? ' users-view-tab--active' : ''}`}
                onClick={() => setUsersView('accounts')}>
                👥 Accounts <span className="users-view-count">{caregivers.length}</span>
              </button>
              <button type="button" role="tab" aria-selected={usersView === 'invites'}
                className={`users-view-tab${usersView === 'invites' ? ' users-view-tab--active' : ''}`}
                onClick={() => setUsersView('invites')}>
                🔑 Invite codes <span className="users-view-count">{activeCodes.length}</span>
              </button>
              <span className="users-view-hint">
                {usersView === 'accounts'
                  ? 'Create accounts here, or use invite codes to let caregivers register themselves.'
                  : 'An invite code lets a caregiver register on their own. Each code works once.'}
              </span>
            </div>

            {usersView === 'accounts' && (
              <>
                <div className="users-stats">
                  <div className="stat-card">
                    <div className="stat-value">{userCounts.total}</div>
                    <div className="stat-label">Total users</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{userCounts.admins}</div>
                    <div className="stat-label">Admins</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{userCounts.caregivers}</div>
                    <div className="stat-label">Caregivers</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value" style={{ color: userCounts.disabled ? 'var(--danger)' : undefined }}>{userCounts.disabled}</div>
                    <div className="stat-label">Disabled</div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>👥 Registered Users</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                        {userFiltersActive ? `${visibleUsers.length} of ${caregivers.length}` : `${caregivers.length} user${caregivers.length !== 1 ? 's' : ''}`}
                      </span>
                      <button type="button" className="btn btn-sm" onClick={() => setCreateUserOpen(true)}
                        style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}>
                        + New user
                      </button>
                    </div>
                  </div>

                  <div className="users-toolbar">
                    <input className="search-input" type="search" placeholder="Search by name or email…"
                      value={userSearch} onChange={e => setUserSearch(e.target.value)} aria-label="Search users" />
                    <div className="chip-group" role="group" aria-label="Filter by role">
                      {[['all', 'All roles'], ['admin', 'Admins'], ['caregiver', 'Caregivers']].map(([v, label]) => (
                        <button key={v} type="button" className={`chip${userRoleFilter === v ? ' chip--active' : ''}`}
                          onClick={() => setUserRoleFilter(v)}>{label}</button>
                      ))}
                    </div>
                    <div className="chip-group" role="group" aria-label="Filter by status">
                      {[['all', 'Any status'], ['active', 'Active'], ['disabled', 'Disabled']].map(([v, label]) => (
                        <button key={v} type="button" className={`chip${userStatusFilter === v ? ' chip--active' : ''}`}
                          onClick={() => setUserStatusFilter(v)}>{label}</button>
                      ))}
                    </div>
                    {userFiltersActive && (
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => { setUserSearch(''); setUserRoleFilter('all'); setUserStatusFilter('all'); }}>
                        Clear
                      </button>
                    )}
                  </div>

                  <div className="card-body" style={{ padding: 0 }}>
                    {caregivers.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-icon">👤</div>
                        <h3>No users yet</h3>
                        <p>Create the first account directly, or generate an invite code so the caregiver can register.</p>
                        <button type="button" className="btn btn-teal" style={{ marginTop: 12 }} onClick={() => setCreateUserOpen(true)}>
                          + New user
                        </button>
                      </div>
                    ) : visibleUsers.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-icon">🔍</div>
                        <h3>No matching users</h3>
                        <p>Try a different search or clear the filters.</p>
                      </div>
                    ) : (
                      <>
                        <div className="admin-table-wrap">
                          <table className="notes-table users-table">
                            <thead>
                              <tr>
                                <th>User</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Joined</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleUsers.map(u => {
                                const name = userDisplayName(u);
                                const me = isCurrentUser(u, user);
                                const hint = COGNITO_STATUS_HINTS[u.userStatus];
                                return (
                                  <tr key={u.username} className={u.status !== 'active' ? 'users-row--disabled' : ''}>
                                    <td>
                                      <div className="user-cell">
                                        <div className={`user-avatar${u.role === 'admin' ? ' user-avatar--admin' : ''}${u.status !== 'active' ? ' user-avatar--disabled' : ''}`}>
                                          {userInitials(u)}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                          <div className="user-name">
                                            {name || <span className="user-name--missing">No name set</span>}
                                            {me && <span className="user-you-tag">You</span>}
                                          </div>
                                          {hint && <div className="user-hint">{hint}</div>}
                                        </div>
                                      </div>
                                    </td>
                                    <td style={{ fontSize: 13, color: 'var(--muted)' }}>{u.email}</td>
                                    <td><span className={`badge badge-${u.role === 'admin' ? 'admin' : 'caregiver'}`}>{u.role === 'admin' ? 'admin' : 'caregiver'}</span></td>
                                    <td><span className={`status-pill status-pill--${u.status === 'active' ? 'active' : 'disabled'}`}>{u.status}</span></td>
                                    <td style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '–'}</td>
                                    <td>
                                      {confirmDeleteUser === u.username ? (
                                        <div className="user-row-actions user-row-actions--confirm">
                                          <span className="user-delete-prompt">Delete {name || u.email}? This cannot be undone.</span>
                                          <button type="button" className="btn btn-danger btn-sm" disabled={deletingUser === u.username}
                                            onClick={() => handleDeleteUser(u)}>
                                            {deletingUser === u.username ? 'Deleting...' : 'Yes, delete'}
                                          </button>
                                          <button type="button" className="btn btn-ghost btn-sm" disabled={deletingUser === u.username}
                                            onClick={() => setConfirmDeleteUser(null)}>
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="user-row-actions">
                                          <button type="button" className="btn btn-ghost btn-sm"
                                            onClick={() => setUserModal({ username: u.username, tab: 'profile' })}>
                                            ✏️ Edit
                                          </button>
                                          <button type="button" className="btn btn-ghost btn-sm"
                                            onClick={() => setUserModal({ username: u.username, tab: 'access' })}>
                                            🔑 Password
                                          </button>
                                          {u.role !== 'admin' && (
                                            <button type="button" className="btn btn-ghost btn-sm"
                                              style={{ color: '#1a6b4a', borderColor: '#1a6b4a' }}
                                              onClick={() => setSmsModalUser(u)}>
                                              SMS
                                            </button>
                                          )}
                                          {!me && (
                                            <button type="button" className="btn btn-ghost btn-sm user-btn-danger-ghost"
                                              title="Permanently delete this account"
                                              onClick={() => setConfirmDeleteUser(u.username)}>
                                              Delete
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="admin-mobile-list">
                          {visibleUsers.map(u => {
                            const name = userDisplayName(u);
                            const me = isCurrentUser(u, user);
                            const hint = COGNITO_STATUS_HINTS[u.userStatus];
                            return (
                              <div key={u.username} className="mobile-note-card" style={{ opacity: u.status !== 'active' ? 0.75 : 1 }}>
                                <div className="user-cell">
                                  <div className={`user-avatar${u.role === 'admin' ? ' user-avatar--admin' : ''}${u.status !== 'active' ? ' user-avatar--disabled' : ''}`}>
                                    {userInitials(u)}
                                  </div>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div className="user-name">
                                      {name || <span className="user-name--missing">No name set</span>}
                                      {me && <span className="user-you-tag">You</span>}
                                    </div>
                                    <div className="mobile-note-meta" style={{ wordBreak: 'break-all' }}>{u.email}</div>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <span className={`badge badge-${u.role === 'admin' ? 'admin' : 'caregiver'}`}>{u.role === 'admin' ? 'admin' : 'caregiver'}</span>
                                  <span className={`status-pill status-pill--${u.status === 'active' ? 'active' : 'disabled'}`}>{u.status}</span>
                                  {u.createdAt && <span className="mobile-note-meta">Joined {new Date(u.createdAt).toLocaleDateString()}</span>}
                                </div>
                                {hint && <div className="user-hint">{hint}</div>}
                                {confirmDeleteUser === u.username ? (
                                  <div className="mobile-note-actions" style={{ flexWrap: 'wrap' }}>
                                    <span className="user-delete-prompt" style={{ flexBasis: '100%' }}>Delete {name || u.email}? This cannot be undone.</span>
                                    <button type="button" className="btn btn-danger btn-sm" style={{ flex: 1 }} disabled={deletingUser === u.username}
                                      onClick={() => handleDeleteUser(u)}>{deletingUser === u.username ? 'Deleting...' : 'Yes, delete'}</button>
                                    <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }} disabled={deletingUser === u.username}
                                      onClick={() => setConfirmDeleteUser(null)}>Cancel</button>
                                  </div>
                                ) : (
                                  <div className="mobile-note-actions">
                                    <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }}
                                      onClick={() => setUserModal({ username: u.username, tab: 'profile' })}>✏️ Edit</button>
                                    <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }}
                                      onClick={() => setUserModal({ username: u.username, tab: 'access' })}>🔑 Password</button>
                                    {u.role !== 'admin' && (
                                      <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1, color: '#1a6b4a', borderColor: '#1a6b4a' }}
                                        onClick={() => setSmsModalUser(u)}>SMS</button>
                                    )}
                                    {!me && (
                                      <button type="button" className="btn btn-ghost btn-sm user-btn-danger-ghost" style={{ flex: 1 }}
                                        onClick={() => setConfirmDeleteUser(u.username)}>Delete</button>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Invite codes view */}
            {usersView === 'invites' && <div className="card">
              <div className="card-header">🔑 Generate Invite Code</div>
              <div className="card-body">
                <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
                  Generate a code to share with a new caregiver. Each code can only be used once.
                </p>
                <form onSubmit={handleGenerate}>
                  {/* Assigned To - full width on top */}
                  <div className="form-group" style={{ marginBottom: 16 }}>
                    <label className="form-label">Assigned To (who is this code for?)</label>
                    <input className="form-input" type="text"
                      value={genForm.assignedTo}
                      onChange={e => setGenForm(f => ({ ...f, assignedTo: e.target.value }))}
                      placeholder="e.g. Jane Smith"
                      style={{ maxWidth: 320 }} />
                  </div>
                  {/* Options row */}
                  <div className="gen-form" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div className="form-group-card">
                      <label className="form-label">Number of Codes</label>
                      <select className="form-input" style={{ width: 120 }}
                        value={genForm.count} onChange={e => setGenForm(f => ({ ...f, count: parseInt(e.target.value) }))}>
                        {[1, 2, 3, 5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div className="form-group-card">
                      <label className="form-label">Role</label>
                      <select className="form-input" style={{ width: 140 }}
                        value={genForm.role} onChange={e => setGenForm(f => ({ ...f, role: e.target.value }))}>
                        <option value="caregiver">Caregiver</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="form-group-card">
                      <label className="form-label">Expires After</label>
                      <select className="form-input" style={{ width: 140 }}
                        value={genForm.expiryDays} onChange={e => setGenForm(f => ({ ...f, expiryDays: parseInt(e.target.value) }))}>
                        <option value={3}>3 days</option>
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={30}>30 days</option>
                      </select>
                    </div>
                    <button className="btn btn-teal" type="submit" disabled={generating}>
                      {generating ? 'Generating…' : '+ Generate Code'}
                    </button>
                  </div>
                </form>

                {/* Newly generated codes */}
                {newCodes.length > 0 && (
                  <div style={{ marginTop: 24, background: 'var(--cream)', borderRadius: 10, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontWeight: 600, color: 'var(--navy)' }}>
                        ✅ {newCodes.length} code{newCodes.length > 1 ? 's' : ''} generated
                        {genForm.assignedTo ? ` for ${genForm.assignedTo}` : ''}. Share with your caregiver:
                      </span>
                      {newCodes.length > 1 && (
                        <button className="btn btn-ghost btn-sm" onClick={copyAll}>📋 Copy All</button>
                      )}
                    </div>
                    <div className="codes-list">
                      {newCodes.map(code => (
                        <button key={code} className="invite-code" onClick={() => copyCode(code)}
                          title="Click to copy"
                          style={{ cursor: 'pointer', border: copied === code ? '1.5px solid var(--teal)' : undefined }}>
                          {copied === code ? '✓ Copied!' : code}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
                      Click the code to copy it. Send to your caregiver via email or text.
                      They register at <strong>notes.homecareofmi.com</strong> using this code.
                    </p>
                  </div>
                )}
              </div>
            </div>}

            {/* Active invite codes */}
            {usersView === 'invites' && <div className="card">
              <div className="card-header">
                ⏳ Active Invite Codes
                <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.8 }}>{activeCodes.length} unused</span>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {activeCodes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">🔑</div>
                    <h3>No active codes</h3>
                    <p>Generate invite codes above to share with caregivers.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop */}
                    <div className="admin-table-wrap">
                      <table className="notes-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Assigned To</th>
                            <th>Role</th>
                            <th>Expires</th>
                            <th>Created By</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCodes.map(c => (
                            <tr key={c.code}>
                              <td><span className="invite-code" style={{ fontSize: 13 }}>{c.code}</span></td>
                              <td style={{ fontWeight: c.assignedTo ? 500 : 400, color: c.assignedTo ? 'var(--navy)' : 'var(--muted)' }}>
                                {c.assignedTo || 'Anyone'}
                              </td>
                              <td><span className={`badge badge-${c.role === 'admin' ? 'admin' : 'caregiver'}`}>{c.role}</span></td>
                              <td style={{ fontSize: 13, color: 'var(--muted)' }}>{new Date(c.expiresAt).toLocaleDateString()}</td>
                              <td style={{ fontSize: 13, color: 'var(--muted)' }}>{c.createdBy}</td>
                              <td style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => copyCode(c.code)}>
                                  {copied === c.code ? '✓ Copied' : '📋 Copy'}
                                </button>
                                <button className="btn btn-sm"
                                  style={{ background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                                  onClick={() => handleDelete(c.code, c.assignedTo)}>
                                  🗑 Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile */}
                    <div className="admin-mobile-list">
                      {activeCodes.map(c => (
                        <div key={c.code} className="mobile-note-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <span className="invite-code" style={{ fontSize: 13 }}>{c.code}</span>
                            <span className={`badge badge-${c.role === 'admin' ? 'admin' : 'caregiver'}`}>{c.role}</span>
                          </div>
                          {c.assignedTo && (
                            <div style={{ fontWeight: 500, color: 'var(--navy)', marginTop: 4 }}>👤 {c.assignedTo}</div>
                          )}
                          <div className="mobile-note-meta">
                            Expires: {new Date(c.expiresAt).toLocaleDateString()} · By: {c.createdBy}
                          </div>
                          <div className="mobile-note-actions">
                            <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }}
                              onClick={() => copyCode(c.code)}>
                              {copied === c.code ? '✓ Copied' : '📋 Copy'}
                            </button>
                            <button className="btn btn-sm" style={{ flex: 1, justifyContent: 'center', background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                              onClick={() => handleDelete(c.code, c.assignedTo)}>
                              🗑 Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>}

            {/* Expired codes */}
            {usersView === 'invites' && expiredCodes.length > 0 && (
              <div className="card">
                <div className="card-header" style={{ opacity: 0.7 }}>
                  🕐 Expired Codes
                  <span style={{ fontSize: 13, fontWeight: 400 }}>{expiredCodes.length} expired</span>
                </div>
                <div className="card-body" style={{ padding: 0 }}>
                  <div className="admin-table-wrap">
                    <table className="notes-table">
                      <thead>
                        <tr><th>Code</th><th>Assigned To</th><th>Role</th><th>Expired</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {expiredCodes.map(c => (
                          <tr key={c.code} style={{ opacity: 0.6 }}>
                            <td><span className="invite-code" style={{ fontSize: 13 }}>{c.code}</span></td>
                            <td style={{ color: 'var(--muted)' }}>{c.assignedTo || 'Anyone'}</td>
                            <td><span className={`badge badge-${c.role === 'admin' ? 'admin' : 'caregiver'}`}>{c.role}</span></td>
                            <td style={{ fontSize: 13, color: 'var(--danger)' }}>{new Date(c.expiresAt).toLocaleDateString()}</td>
                            <td>
                              <button className="btn btn-sm"
                                style={{ background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                                onClick={() => handleDelete(c.code, c.assignedTo)}>
                                🗑 Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="admin-mobile-list">
                    {expiredCodes.map(c => (
                      <div key={c.code} className="mobile-note-card" style={{ opacity: 0.7 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span className="invite-code" style={{ fontSize: 13 }}>{c.code}</span>
                          <span style={{ color: 'var(--danger)', fontSize: 12 }}>Expired {new Date(c.expiresAt).toLocaleDateString()}</span>
                        </div>
                        {c.assignedTo && <div className="mobile-note-meta">👤 {c.assignedTo}</div>}
                        <div className="mobile-note-actions">
                          <button className="btn btn-sm" style={{ flex: 1, background: '#fdf2f2', color: 'var(--danger)', border: '1px solid #f5c6c6' }}
                            onClick={() => handleDelete(c.code, c.assignedTo)}>🗑 Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {toast && <div className={`toast ${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div>}

      {/* Create User Modal */}
      {createUserOpen && (
        <CreateUserModal
          onClose={() => setCreateUserOpen(false)}
          onCreated={(u) => setCaregivers(prev => (prev.some(x => x.username === u.username) ? prev : [...prev, u]))}
          showToast={showToast}
        />
      )}

      {/* User Management Modal */}
      {userModalTarget && (
        <UserModal
          key={userModalTarget.username}
          target={userModalTarget}
          currentUser={user}
          initialTab={userModal.tab}
          onClose={() => setUserModal(null)}
          onUpdated={applyUserUpdate}
          onOpenSms={(u) => { setUserModal(null); setSmsModalUser(u); }}
          showToast={showToast}
        />
      )}

      {/* SMS Settings Modal */}
      {smsModalUser && (
        <SmsModal
          caregiver={smsModalUser}
          onClose={() => setSmsModalUser(null)}
          showToast={showToast}
        />
      )}

      {/* T&A Reports Modal */}
      {taModal.open && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, #1a4a2b, #2d8e5a)', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>T&amp;A Reports</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>{taModal.clientName}</div>
              </div>
              <button onClick={() => setTaModal({ open: false, clientId: null, clientName: '' })}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '6px 12px', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                Upload Time &amp; Attendance reports for this client. Saved reports can be selected in AI Agent EVV without re-uploading.
              </p>

              {/* Upload button */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  cursor: uploadingTaReport ? 'not-allowed' : 'pointer',
                  padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                  background: uploadingTaReport ? '#e8edf2' : 'linear-gradient(135deg, #1a4a2b, #2d8e5a)',
                  color: uploadingTaReport ? 'var(--muted)' : '#fff', opacity: uploadingTaReport ? 0.7 : 1,
                }}>
                  <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }} disabled={uploadingTaReport} onChange={handleTaUpload} />
                  {uploadingTaReport
                    ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /> Uploading…</>
                    : <>📂 Upload T&amp;A Report</>}
                </label>
                <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--muted)' }}>CSV or Excel (.xlsx) · max 10 MB</span>
                {taReportError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)', background: '#fdf2f2', padding: '8px 12px', borderRadius: 6 }}>{taReportError}</div>
                )}
              </div>

              {/* Saved reports list */}
              {loadingTaReports ? (
                <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : taReports.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <div className="empty-state-icon">📄</div>
                  <h3>No reports uploaded yet</h3>
                  <p>Upload a T&amp;A report above to save it for reuse.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '50%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '6%' }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: 'var(--cream)' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)' }}>File Name</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Uploaded</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)' }}>By</th>
                        <th style={{ padding: '8px 12px', border: '1px solid var(--border)' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {taReports.map(r => (
                        <tr key={r.reportId}>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', fontWeight: 500, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fileName}>{r.fileName}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                            {new Date(r.uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.uploadedBy}>{r.uploadedBy}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                            <button onClick={() => handleTaDelete(r.reportId, r.fileName)}
                              style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 15 }}>🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setTaModal({ open: false, clientId: null, clientName: '' })}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Prior Authorization Modal */}
      {priorAuthModal.open && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1080, maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg, #7a4a1a, #c98a3a)', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Prior Authorizations</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>{priorAuthModal.clientName}</div>
              </div>
              <button onClick={() => setPriorAuthModal({ open: false, clientId: null, clientName: '' })}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '6px 12px', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                Upload a Prior Authorization PDF. The system will extract the key fields with AI for you to review before saving.
              </p>

              {/* Upload button */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  cursor: uploadingPriorAuth ? 'not-allowed' : 'pointer',
                  padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                  background: uploadingPriorAuth ? '#e8edf2' : 'linear-gradient(135deg, #7a4a1a, #c98a3a)',
                  color: uploadingPriorAuth ? 'var(--muted)' : '#fff', opacity: uploadingPriorAuth ? 0.7 : 1,
                }}>
                  <input type="file" accept=".pdf,application/pdf"
                    style={{ display: 'none' }} disabled={uploadingPriorAuth} onChange={handlePriorAuthUpload} />
                  {uploadingPriorAuth
                    ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /> Analyzing…</>
                    : <>📄 Upload Prior Auth PDF</>}
                </label>
                <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--muted)' }}>PDF only · max 10 MB</span>
                {priorAuthError && (
                  <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)', background: '#fdf2f2', padding: '8px 12px', borderRadius: 6 }}>{priorAuthError}</div>
                )}
              </div>

              {/* Review form for just-parsed, unconfirmed upload */}
              {priorAuthReview && (
                <div style={{ marginBottom: 24, padding: 16, borderRadius: 8, background: '#fff8ef', border: '1px solid #f0d9ae' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Review extracted data before saving</div>
                  {(priorAuthReview.clientName || priorAuthReview.memberId) && (
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
                      Document shows: {priorAuthReview.clientName || '—'} {priorAuthReview.memberId ? `(Member ID: ${priorAuthReview.memberId})` : ''} — confirm this matches the selected client.
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                      <label className="form-label">Auth # <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input className="form-input" type="text" value={priorAuthReview.authNumber || ''}
                        onChange={e => updatePriorAuthReview('authNumber', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                      <label className="form-label">Payer</label>
                      <input className="form-input" type="text" value={priorAuthReview.payer || ''}
                        onChange={e => updatePriorAuthReview('payer', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                      <label className="form-label">Service Type</label>
                      <input className="form-input" type="text" value={priorAuthReview.serviceType || ''}
                        onChange={e => updatePriorAuthReview('serviceType', e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <div className="form-group-card" style={{ flex: '1 1 120px' }}>
                      <label className="form-label">Units Authorized</label>
                      <input className="form-input" type="text" value={priorAuthReview.unitsAuthorized || ''}
                        onChange={e => updatePriorAuthReview('unitsAuthorized', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Units Period</label>
                      <input className="form-input" type="text" value={priorAuthReview.unitsPeriod || ''}
                        onChange={e => updatePriorAuthReview('unitsPeriod', e.target.value)} placeholder="e.g. per month" />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Units Approved per Week</label>
                      <input className="form-input" type="text" value={priorAuthReview.unitsPerWeek || ''}
                        onChange={e => updatePriorAuthReview('unitsPerWeek', e.target.value)}
                        style={unitsToHours(priorAuthReview.unitsPerWeek) === null ? { borderColor: 'var(--danger)' } : undefined} />
                      {unitsToHours(priorAuthReview.unitsPerWeek) === null ? (
                        <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Must be a non-negative number</div>
                      ) : priorAuthReview.unitsPerWeek !== '' && priorAuthReview.unitsPerWeek != null ? (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                          {unitsToHours(priorAuthReview.unitsPerWeek)} hours/week approved
                        </div>
                      ) : null}
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Start Date <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input className="form-input" type="date" value={priorAuthReview.startDate || ''}
                        onChange={e => updatePriorAuthReview('startDate', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">End Date <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input className="form-input" type="date" value={priorAuthReview.endDate || ''}
                        onChange={e => updatePriorAuthReview('endDate', e.target.value)} />
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Care details for the client record</div>
                  <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>Taken from the document when it lists them. Blank fields can be typed in now or later on the client overview.</p>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Member ID</label>
                      <input className="form-input" type="text" value={priorAuthReview.memberId || ''}
                        onChange={e => updatePriorAuthReview('memberId', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Diagnosis Code</label>
                      <input className="form-input" type="text" value={priorAuthReview.diagnosisCode || ''}
                        onChange={e => updatePriorAuthReview('diagnosisCode', e.target.value)} placeholder="ICD-10" />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 160px' }}>
                      <label className="form-label">Case Manager Name</label>
                      <input className="form-input" type="text" value={priorAuthReview.caseManagerName || ''}
                        onChange={e => updatePriorAuthReview('caseManagerName', e.target.value)} />
                    </div>
                    <div className="form-group-card" style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Case Manager Phone</label>
                      <input className="form-input" type="tel" value={priorAuthReview.caseManagerPhone || ''}
                        onChange={e => updatePriorAuthReview('caseManagerPhone', e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-teal" type="button" disabled={savingPriorAuth} onClick={handleConfirmPriorAuth}>
                      {savingPriorAuth ? 'Saving…' : 'Confirm & Save'}
                    </button>
                    <button className="btn btn-ghost" type="button" onClick={() => setPriorAuthReview(null)}>Discard</button>
                  </div>
                </div>
              )}

              {/* History */}
              {loadingPriorAuths ? (
                <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : priorAuths.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <div className="empty-state-icon">📄</div>
                  <h3>No authorizations on file</h3>
                  <p>Upload a Prior Authorization PDF above to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--cream)' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)' }}>Auth #</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)' }}>Status</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Start</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>End</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Units</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Units/Week</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Hours/Week</th>
                        <th style={{ padding: '8px 12px', border: '1px solid var(--border)' }}></th>
                        <th style={{ padding: '8px 12px', border: '1px solid var(--border)' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {priorAuths.map(a => (
                        <tr key={a.authId}>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', fontWeight: 500 }}>{a.authNumber || '—'}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 600, borderRadius: 10, padding: '2px 8px',
                              background: a.status === 'active' ? '#e6f4ea' : a.status === 'pending_review' ? '#fff4e0' : '#eee',
                              color: a.status === 'active' ? '#1a6b4a' : a.status === 'pending_review' ? '#8e5a2d' : 'var(--muted)',
                            }}>
                              {a.status === 'pending_review' ? 'Pending review' : a.status === 'active' ? 'Active' : 'Superseded'}
                            </span>
                            {a.status === 'active' && a.snoozedUntil && (
                              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Snoozed until {formatDate(a.snoozedUntil)}</div>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{formatDate(a.startDate)}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{formatDate(a.endDate)}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{a.unitsAuthorized ? `${a.unitsAuthorized}${a.unitsPeriod ? ` ${a.unitsPeriod}` : ''}` : 'Not set'}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{a.unitsPerWeek || 'Not set'}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{a.hoursPerWeek || 'Not set'}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)' }}>
                            {a.status === 'active' && (
                              <select defaultValue="" onChange={e => { if (e.target.value) { handleSnoozePriorAuth(priorAuthModal.clientId, a.authId, Number(e.target.value)); e.target.value = ''; } }}
                                style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--border)' }}>
                                <option value="" disabled>Snooze…</option>
                                <option value="7">7 days</option>
                                <option value="14">14 days</option>
                                <option value="30">30 days</option>
                                <option value="60">60 days</option>
                              </select>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                            <button onClick={() => handleDeletePriorAuth(a.authId, a.authNumber)}
                              style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 15 }}>🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setPriorAuthModal({ open: false, clientId: null, clientName: '' })}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Assessment Modal */}
      {showAssessmentModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto', boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#1a2b4a,#2d5a8e)', borderRadius: '12px 12px 0 0' }}>
              <div style={{ color: '#fff' }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Functional Assessment</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>{clients.find(c => c.clientId === assessmentClientId)?.clientName || ''}</div>
              </div>
              <button onClick={() => setShowAssessmentModal(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '6px 12px', fontSize: 18 }}>✕</button>
            </div>

            {loadingAssessment ? (
              <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
            ) : (
              <form onSubmit={handleSaveAssessment}>
                <div style={{ padding: '20px 24px' }}>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                    Upload the Functional Assessment Summary PDF or photo — the AI will extract all tasks automatically. You can also enter or edit them manually below.
                  </p>

                  {/* File upload */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, cursor: parsingFile ? 'not-allowed' : 'pointer',
                      padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: 14,
                      background: parsingFile ? '#e8edf2' : 'linear-gradient(135deg,#1a2b4a,#2d5a8e)',
                      color: parsingFile ? 'var(--muted)' : '#fff', border: 'none',
                      opacity: parsingFile ? 0.7 : 1, transition: 'opacity 0.2s',
                    }}>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                        style={{ display: 'none' }} disabled={parsingFile}
                        onChange={handleAssessmentFileUpload} />
                      {parsingFile ? (
                        <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Analyzing…</>
                      ) : (
                        <> Upload PDF or Image</>
                      )}
                    </label>
                    <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--muted)' }}>
                      JPEG, PNG, WebP, or PDF · max 7 MB
                    </span>
                    {parseError && (
                      <div style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)', background: '#fdf2f2', padding: '8px 12px', borderRadius: 6 }}>
                        {parseError}
                      </div>
                    )}
                  </div>

                  {/* Task table */}
                  <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--cream)' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', minWidth: 160 }}>Task</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', minWidth: 90 }}>Time/Day (HH:MM)</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', minWidth: 170 }}>Frequency</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, border: '1px solid var(--border)', minWidth: 100 }}>Time/Month (HH:MM)</th>
                          <th style={{ padding: '8px 10px', border: '1px solid var(--border)', width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessmentTasks.map((row, i) => (
                          <tr key={i}>
                            <td style={{ border: '1px solid var(--border)', padding: 6 }}>
                              <input className="form-input" style={{ margin: 0, fontSize: 13 }} type="text" value={row.task}
                                onChange={e => updateAssessmentTask(i, 'task', e.target.value)}
                                placeholder="e.g. Bathing" />
                            </td>
                            <td style={{ border: '1px solid var(--border)', padding: 6 }}>
                              <input className="form-input" style={{ margin: 0, fontSize: 13 }} type="text" value={row.timePerDay}
                                onChange={e => updateAssessmentTask(i, 'timePerDay', e.target.value)}
                                placeholder="00:16" />
                            </td>
                            <td style={{ border: '1px solid var(--border)', padding: 6 }}>
                              <select className="form-input" style={{ margin: 0, fontSize: 13 }} value={row.frequency}
                                onChange={e => updateAssessmentTask(i, 'frequency', e.target.value)}>
                                {FREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                              </select>
                            </td>
                            <td style={{ border: '1px solid var(--border)', padding: 6 }}>
                              <input className="form-input" style={{ margin: 0, fontSize: 13 }} type="text" value={row.timePerMonth}
                                onChange={e => updateAssessmentTask(i, 'timePerMonth', e.target.value)}
                                placeholder="08:02" />
                            </td>
                            <td style={{ border: '1px solid var(--border)', padding: 6, textAlign: 'center' }}>
                              <button type="button" onClick={() => removeAssessmentTask(i)}
                                style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, padding: 2 }}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button type="button" className="btn btn-ghost btn-sm" onClick={addAssessmentTask} style={{ marginBottom: 20 }}>
                    + Add Task
                  </button>

                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="form-group-card" style={{ flex: '0 0 180px' }}>
                      <label className="form-label">Total Monthly Time (HH:MM) <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input className="form-input" type="text" value={assessmentTotal}
                        onChange={e => setAssessmentTotal(e.target.value)} placeholder="67:12" />
                    </div>
                  </div>
                </div>

                <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setShowAssessmentModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-teal" disabled={savingAssessment}
                    style={{ background: 'linear-gradient(135deg,#1a2b4a,#2d5a8e)', color: '#fff', border: 'none' }}>
                    {savingAssessment ? 'Saving…' : 'Save Assessment'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
